/**
    * Canvas management mixin
    * Provides functionality for managing the canvas, panels, and objects within the Mikomic editor.
 */
import { markRaw } from 'vue';

export default {
    data() {
        return {

            //! canvas
            // Konva core elements
            stage: null,
            panels_layer: null,   // Layer for all panels
            ui_layer: null,   // Layer for ui indicators
            transformer: null,  
            panel_color: '#ffffff',

            object_scale_value: 1,
            object_rotation_value: 0,
            resize_timer: null,
            _transform_update_pending: false, // Flag for throttling transform updates
            
            // Panels
            panels: [],
            selected_panel: null,
            last_selected_panel: null, // Track last selected panel for gallery insertion

            // Canvas properties
            canvas_width: 800,
            canvas_height: 1200, // Comic page format
            outline_width: 2,
            outline_color: '#000000', // Added outline color
            background_color: '#ffffff',
            page_margin: 20,                       
            
            // Aspect ratio options instead of fixed formats
            aspect_ratios: [
                { id: 'comic_portrait', name: 'Comic Portrait', ratio: 2/3, base_size: 'medium' },
                { id: 'comic_landscape', name: 'Comic Landscape', ratio: 3/2, base_size: 'medium' },
                { id: 'a4_portrait', name: 'A4 Portrait', ratio: 1/1.414, base_size: 'medium' },
                { id: 'a4_landscape', name: 'A4 Landscape', ratio: 1.414/1, base_size: 'medium' },
                { id: 'square', name: 'Square', ratio: 1/1, base_size: 'medium' },
                { id: 'widescreen', name: 'Widescreen', ratio: 16/9, base_size: 'medium' },
                { id: 'social_media', name: 'Social Media', ratio: 4/5, base_size: 'medium' },
                { id: 'custom', name: 'Custom Size', ratio: null, base_size: 'custom' }
            ],
            current_aspect_ratio: 'comic_portrait', // Default aspect ratio
            
            // Base sizes for quality levels
            base_sizes: {
                small: { width: 600, height: 900 },
                medium: { width: 800, height: 1200 }, 
                large: { width: 1200, height: 1800 },
                xlarge: { width: 1600, height: 2400 }
            },

            // Add zoom-related properties
            canvas_zoom: 1,
            min_zoom: 0.1, 
            max_zoom: 5,
            initial_auto_zoom: true, // Flag to indicate if zoom has been auto-adjusted
            zoom_margin_percent: 5, // Percent margin around canvas for auto-zoom
            zoom_animation_speed: 0.15, // For smooth zooming 
            wheel_zoom_sensitivity: 0.3, // Increased sensitivity for wheel zoom

            // Temporary state for the 'New Document' dialog
            new_doc_options: {
                aspect_ratio_id: 'comic_portrait',
                base_size: 'medium',
                page_margin: 20,
                outline_width: 2,
                outline_color: '#000000',
                background_color: '#ffffff',
                custom_width: 800,
                custom_height: 1200
            },
            clipboard_object_data: null, // Store copied object data
            clipboard_multi_object_data: [], // Store multiple copied objects data
            is_performing_cut_cleanup: false, // Flag to prevent history recording during cut cleanup
            // Panel specific properties
            
            selected_panel_background_color: '#ffffff',
            panel_transform_active: false,
            panel_edit_mode_active: false,
            editing_panel: null,
            panel_edit_proportional_mode: true, // Toggle for proportional vs linear movement

            // Edge drag blocking state - persistent during drag operations
            edge_drag_blocked_handles: new Map(), // Map of handle_id -> { blocked: true/false, initial_direction: vector }
            current_drag_handle: null, // Currently dragging handle reference

        };
    },
    
    methods: {

        initialize_canvas() { 
            // Get container dimensions instead of using fixed canvas dimensions
            const container = document.getElementById('canvas_wrapper');
            const container_width = container.offsetWidth;
            const container_height = container.offsetHeight;
            
            // Initialize Stage with container dimensions, not canvas dimensions
            this.stage = new Konva.Stage({
                container: 'canvas_wrapper',
                width: container_width,
                height: container_height
            });
            
            // Create main layers
            this.panels_layer = new Konva.Layer();
            this.ui_layer = new Konva.Layer();
            //this.ui_layer = new Konva.FastLayer();
            
            // Add clipping to panels_layer to prevent content from showing outside canvas bounds
            this.panels_layer.clipFunc((ctx) => {
                ctx.rect(0, 0, this.canvas_width, this.canvas_height);
            });
            
            // Add layers to stage
            this.stage.add(this.panels_layer);
            this.stage.add(this.ui_layer);
            
            // Set background (using canvas dimensions, not stage dimensions)
            const background = new Konva.Rect({
                x: 0,
                y: 0,
                width: this.canvas_width,
                height: this.canvas_height,
                fill: this.background_color,
                name: 'background'
            });
            this.panels_layer.add(background);
            
            // Update clipping when canvas dimensions change
            this.update_canvas_clipping();
            
            // Create initial panel with is_initial_layout flag set to true
            this.reset_layout(true);
            
            // Initialize transformer - add to global layer for visibility
            this.transformer = new Konva.Transformer({
                rotateEnabled: true,
                shouldOverdrawWholeArea: true,
                ignoreStroke: false,
                keepRatio: this.preferences.editor.transformer_keep_ratio,
            });
            this.ui_layer.add(this.transformer);
            
            // Set up event listeners
            this.setup_event_listeners();
            
            // Set up drag and drop for external images
            this.setup_drag_and_drop();
            
            // Set up context menu for stage (mouse right-click)
            this.stage.on('contextmenu', (e) => {
                this.handle_context_menu(e);
            });
            
            // Set up long press for touch devices (context menu)
            let touch_timer = null;
            let touch_start_pos = null;
            let touch_moved = false;
            
            this.stage.on('touchstart', (e) => {
                // Clear any existing timer
                if (touch_timer) {
                    clearTimeout(touch_timer);
                    touch_timer = null;
                }
                
                // Store initial touch position
                const pos = this.stage.getPointerPosition();
                touch_start_pos = pos;
                touch_moved = false;
                
                // Set timer for long press (500ms)
                touch_timer = setTimeout(() => {
                    if (!touch_moved && touch_start_pos) {
                        // Trigger context menu for long press
                        this.handle_context_menu(e);
                    }
                    touch_timer = null;
                }, 500);
            });
            
            this.stage.on('touchmove', (e) => {
                // Check if touch moved significantly
                if (touch_start_pos) {
                    const current_pos = this.stage.getPointerPosition();
                    const distance = Math.sqrt(
                        Math.pow(current_pos.x - touch_start_pos.x, 2) + 
                        Math.pow(current_pos.y - touch_start_pos.y, 2)
                    );
                    
                    // If moved more than 10 pixels, cancel long press
                    if (distance > 10) {
                        touch_moved = true;
                        if (touch_timer) {
                            clearTimeout(touch_timer);
                            touch_timer = null;
                        }
                    }
                }
            });
            
            this.stage.on('touchend touchcancel', (e) => {
                // Clear timer on touch end
                if (touch_timer) {
                    clearTimeout(touch_timer);
                    touch_timer = null;
                }
                touch_start_pos = null;
                touch_moved = false;
            });
            
            // Hide context menu on click elsewhere
            document.addEventListener('click', (e) => {
                if (this.show_context_menu) {
                    this.hide_context_menu();
                }
            });
            
            // Apply auto zoom after initialization
            this.$nextTick(() => {
                this.auto_fit_canvas_to_screen();
                this.select_global_layers();                
            });
        },
        
        handle_context_menu(e) {
            // Don't interfere with existing context menus (like node editing)
            if (e.target.hasName && (e.target.hasName('anchor_handle') || e.target.hasName('cp_handle') || e.target.hasName('balloon_handle'))) {
                return; // Let balloon_tool handle these
            }
            
            e.evt.preventDefault();
            
            const target = e.target;
            const target_element = target.name();
            let object_to_show_menu = null;
            
            // Determine which object to use for menu context
            if (target_element === 'back' && this.selected_object) {
                // When clicking on transformer overlay, use the selected object
                object_to_show_menu = this.selected_object;
            } 
            else if (target_element === 'panel_image') {
                // Images get context menu
                object_to_show_menu = target;
            } 
            else if (target_element === 'text_content') {
                // Find the parent standalone text group
                let parent = target.getParent();
                while (parent) {
                    if (parent.name() === 'standalone_text') {
                        object_to_show_menu = parent;
                        break;
                    }
                    parent = parent.getParent();
                }
            } else if (target_element === 'balloon_shape' || target_element === 'balloon_text') {
                // Find the parent balloon group
                let parent = target.getParent();
                while (parent) {
                    if (parent.name() === 'panel_balloon') {
                        object_to_show_menu = parent;
                        break;
                    }
                    parent = parent.getParent();
                }
            }
            // If no specific object found but click is in stage, still show menu (null target)
            
            // Always show context menu (object_to_show_menu can be null for general actions)
            const mouse_pos = this.stage.getPointerPosition();
            const container_rect = this.stage.container().getBoundingClientRect();
            this.show_context_menu_at(
                mouse_pos.x + container_rect.left,
                mouse_pos.y + container_rect.top,
                object_to_show_menu
            );
        },
        
        reset_layout(is_initial_layout = false) {
            if (!this.stage) return;
            
            // Deselect any objects first
            this.deselect_object();
            
            // Clear existing panels
            this.panels.forEach(panel => panel.destroy());
            this.panels = [];
            
            // Calculate panel dimensions
            const panel_width = this.canvas_width - (this.page_margin * 2);
            const panel_height = this.canvas_height - (this.page_margin * 2);           
            // Create a new panel group
            const panel = new Konva.Group({
                 x: this.page_margin,
                 y: this.page_margin,
                 name: 'panel_group',
                 id: 'panel_' + Date.now()
								   
             });
            
            // Add panel attribute for identification
            panel.setAttr('is_panel', true);
            panel.setAttr('respect_margins', true); // Default to respecting margins
            
            // Define polygon points for the panel boundary
            const points = [
                 0, 0,
                 panel_width, 0,
                 panel_width, panel_height,
                 0, panel_height
             ];
            
            // 1. Background fill (bottom layer)
            const background = new Konva.Line({
                points: points,
                closed: true,
                fill: this.panel_color,
                name: 'panel_background'
            });
            panel.add(background);
            
            // 2. Content group (middle layer) - will contain multiple images
            const content_group = new Konva.Group({
                name: 'content_group',
                listening: true // Ensure group listens for events
            });
            
            // Set clip function for content
            content_group.clipFunc((ctx) => {
                ctx.beginPath();
                for (let i = 0; i < points.length; i += 2) {
                    const x = points[i];
                    const y = points[i + 1];
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
            });
            panel.add(content_group);
            
            // 3. Outline (top layer) - single outline on top
            const outline = new Konva.Line({
                points: points,
                closed: true,
                stroke: this.outline_color,
                strokeWidth: this.outline_width,
                fill: null,
                name: 'panel_outline',
                listening: false // Critical: outline should not capture events
            });
            panel.add(outline);
            
            // Store references for easy access
            panel.outline = outline;
            panel.background = background;
            panel.content_group = content_group;
            panel.points = points;
            
            // Add panel to layer
            this.panels_layer.add(panel);
            this.panels.push(panel);

            //! global data
            this.create_global_layer();
            
            // Render
            this.redraw();

            // Only add to history if this is NOT the initial layout
            if (!is_initial_layout) {
                this.push_to_history('reset_layout');
            } else {
                // For initial layout, initialize MKC2 container using the same logic as new document
                this.initialize_mkc2_structure();
                
                // Create initial history entry without marking document as changed
                this.history_stack = [this.create_snapshot()];
                this.document_has_unsaved_changes = false;
            }
        },

        initialize_mkc2_structure() {
            // Extract MKC2 initialization logic from create_new_document_with_options
            const current_time = new Date().toISOString();
            this.container_data = {
                format_version: "2.0",
                created_at: current_time,
                last_modified: current_time,
                chapter_info: {
                    title: "New Chapter",
                    description: "",
                    current_page_index: 0
                },
                pages: [
                    {
                        page_title: "Page 1",
                        page_created_at: current_time,
                        page_last_modified: current_time,
                        mkc_data: null // Will be populated on first save
                    }
                ]
            };
            this.is_mkc2_container = true;
            this.current_page_index = 0;
        },     
          
        
        select_panel(panel, deselect_first = true) {
            if (this.selected_panel === panel) return;
            
            if (deselect_first) {
                // Deselect previous panel
                this.deselect_panel();
                this.deselect_subtool(); 
            }            
                       
            
            // Select new panel
            this.selected_panel = panel;

            // Track last selected panel for gallery insertion BEFORE deselecting
            if (panel && panel.getAttr('is_global') !== true) {
                this.last_selected_panel = panel;
            }

            this.selected_blend_mode = 'normal';
            this.selected_opacity = 100;
            this.selected_opacity_text = '100%';
            
            // Update panel background color from selected panel
            this.selected_panel_background_color = this.get_selected_panel_background_color();
            
            // Reset panel transform state when changing panels
            this.panel_transform_active = false;
            
            // Highlight the outline with blue (overrides the current outline_color for selection)
            if (panel.outline) {
                panel.outline.stroke('#0096ff'); // Selection highlight color
                panel.outline.strokeWidth(this.outline_width + 1); // Slightly thicker when selected
            }
            
            this.panels_layer.draw();

            // Draw both layers to ensure proper rendering
            this.redraw();
        },

        deselect_panel() {
            if (!this.selected_panel) return;
            
            // Exit panel edit mode if active
            if (this.panel_edit_mode_active) {
                this.exit_panel_edit_mode();
            }
            
            // Reset panel transform state
            this.panel_transform_active = false;
            this.hide_transformer();
            
            // Reset outline color to the current setting and original width (except for global panel)
            if (this.selected_panel.outline && this.selected_panel.getAttr('is_global') !== true) {
                this.selected_panel.outline.stroke(this.outline_color);
                this.selected_panel.outline.strokeWidth(this.outline_width);
            }

            //select global panel
            const global_panel = this.panels.find(panel => panel.getAttr('is_global'));
            if (global_panel) {
                this.selected_panel = global_panel;
            } else {
                this.selected_panel = null;
            }

            this.panels_layer.draw(); 
            
        },

        add_object_to_array(object) {   
            if (this.selected_object_array.indexOf(object) === -1) {
                this.selected_object_array.push(object);
            }
        },

        select_object(object) {                
            if (!object) return;

            // If entering balloon edit mode, handle it before general selection
            if (this.balloon_edit_mode_active && object !== this.editing_balloon) {
                 //console.log("Selecting different object while in balloon edit mode. Exiting edit mode.");
                 this.exit_balloon_edit_mode(); // Exit edit mode first
                 // Continue with selecting the new object below
            }

            if(this.balloon_edit_mode_active){
                // If clicking on a handle within the editing balloon, allow it
                if (object.name() === 'balloon_handle' || object.name() === 'cp_in_handle' || object.name() === 'cp_out_handle') {
                   // Do nothing, let the handle's own event listener manage it
                   return;
                }
                // If clicking elsewhere, exit edit mode
                this.hide_transformer();
                this.exit_balloon_edit_mode(); // Exit edit mode if clicking outside handles
                // Do not proceed to select the clicked object immediately after exiting edit mode
                // Let the user click again if they want to select something else.
                this.redraw();
                return;
            }

            // Don't select if the object is locked
            if (object.getAttr('locked') === true) {
                if (this.selected_object) {
                    this.deselect_object(false); // Deselect without full UI reset yet
                }
                return; // Stop selection process
            }


            // If already selected, just ensure transformer is visible and attached
            if (this.selected_object === object) {
                //console.log('Object already selected:', object.id());
                this.show_transformer(object); // Ensure transformer is visible
                this.redraw();
                return; // No need to re-select or re-attach listeners
            }

            // Check if we're selecting a balloon handle, treat differently
            if (object.name() === 'balloon_handle') {
                // Don't change selection, just allow the handle to be dragged
                return;
            }

            // --- Deselect previous object ---
            // Pass false to prevent immediate UI reset (like layer selection)
            // which will be handled after the new object is selected.
            this.deselect_object(false);

            // --- Select new object ---
            this.selected_object = object; // Assign the (raw) Konva object
            this.add_object_to_array(object);

            // Update UI state based on the selected object's properties
            // This now sets object_scale_value and object_rotation_value
            this.update_ui_from_selected_object();


            const object_type = object.getAttr('object_type') || 'image';

            // For balloon objects, extract original fill color and opacity, handle edit mode
            if (object_type === 'balloon') {
                
                //console.log('Selecting balloon object:', object);
                const current_fill = object.shape.fill();
                let original_fill = object.getAttr('original_fill');

                // If no original fill is stored, derive it from current fill
                if (!original_fill) {
                    if (current_fill && current_fill.startsWith('rgba')) {
                        // Extract the RGB components from existing rgba
                        const match = current_fill.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
                        if (match) {
                            const r = parseInt(match[1], 10);
                            const g = parseInt(match[2], 10);
                            const b = parseInt(match[3], 10);
                            const alpha = parseFloat(match[4]);

                            // Store the hex version of the color as original_fill
                            original_fill = this.rgb_to_hex(r, g, b);
                            object.setAttr('original_fill', original_fill);

                            // Update balloon_fill_alpha
                            this.balloon_fill_alpha = alpha;
                            object.setAttr('fill_opacity', alpha); // Ensure consistency
                        } else {
                            // Fallback if regex doesn't match
                            object.setAttr('original_fill', current_fill);
                            this.balloon_fill_alpha = object.getAttr('fill_opacity') || 1;
                        }
                    } else {
                        // For hex or named colors, just store as is
                        object.setAttr('original_fill', current_fill);
                        this.balloon_fill_alpha = object.getAttr('fill_opacity') || 1;
                    }
                } else {
                    // Use stored opacity
                    this.balloon_fill_alpha = object.getAttr('fill_opacity') || 1;
                }
                
                // If balloon is in edit mode, exit edit mode
                if (object.getAttr('editing_mode')) {
                    this.remove_balloon_edit_handles(object);
                    object.setAttr('editing_mode', false);
                    
                    // Update global flag
                    this.balloon_edit_mode_active = false;
                }


                // Position the floating balloon toolbar
                this.$nextTick(() => {
                    this.update_balloon_toolbar_position(object);
                    // Add listener for when the balloon moves
                    object.off('dragmove.toolbar'); // Ensure no duplicates
                    object.on('dragmove.toolbar', () => {
                        this.update_balloon_toolbar_position(object);
                    });
                     // Also update on transform
                    object.off('transform.toolbar').on('transform.toolbar', () => {
                        this.update_balloon_toolbar_position(object);
                    });
                });
            }

            // Update selected_layer based on the new selected_object
            // Use nextTick to ensure Vue has processed the selection change
            this.$nextTick(() => {
                const layer = this.panel_layers.find(layer => layer.id === object.id());
                this.selected_layer = layer || null;
                // Now update blend/opacity controls based on the potentially new layer context
                // This needs to happen *after* selected_layer is potentially updated
                this.set_object_blend_mode_and_opacity(object);
            });


            // Attach transformer to the object
            this.show_transformer(object);
            this.show_object_specific_controls(object_type);
            
            // Draw both layers to ensure proper rendering
            this.redraw();

            //End of selection.
            //Drag can or cannot start from here

            //clear previous listeners
            object.off('dragstart.miko_drag');
            object.off('dragend.miko_dragend');
            object.off('transformstart.miko_trans');
            object.off('transformend.miko_trans');
            object.off('transform.miko_trans'); // Ensure previous transform listener is also cleared


            object.on('dragstart.miko_drag', () => {
                //this.hide_transformer(object); 

                //console.log('Dragging object');
                this.show_minimal_transformer(this.selected_object); 
                
                // if (object_type === 'balloon') {
                //     this.hide_transformer(this.selected_object); //before losing the reference                    
                // }
                // else {
                //     this.show_minimal_transformer(this.selected_object); //before losing the reference
                // }
            
            });

            
            object.on('dragend.miko_dragend', () => {
                // Check if cross-panel dragging is enabled and handle panel transfer
                if (this.enable_cross_panel_dragging) {
                    this.handle_cross_panel_transfer(object);
                }
                
                this.show_transformer(this.selected_object); //after getting the reference                
                // Update UI state after drag (position might have changed) - Added based on previous correct logic
                this.update_ui_from_selected_object();
                this.push_to_history('move_object');
                // Update balloon toolbar after drag
                if (object_type === 'balloon') {
                    this.update_balloon_toolbar_position(object);
                }
            });



            object.on('transformstart.miko_trans', (e) => {
                //console.log('Transforming object:', this.selected_object.attrs.name);

                if (object_type === 'balloon') {
                    // this.text_editing.active = true;
                    // this.enter_text_adjust_mode();
                    // this.exit_text_adjust_mode();
                }
                
            });

            object.on('transform.miko_trans', (e) => {
                if (!this._transform_update_pending) {
                    this._transform_update_pending = true;
                    requestAnimationFrame(() => {
                        const node = e.target;
                        // Apply transform to all objects in the array
                        if (this.selected_object_array && this.selected_object_array.length > 0) {
                            this.selected_object_array.forEach(obj => {

                                if (obj.getAttr('object_type') === 'standalone_text' || obj.getAttr('object_type') === 'balloon') {
                                    this.transformer.flipEnabled(false); // Disable flipping
                                }
                                
                                // Update balloon toolbar and bake transforms for balloons
                                if (obj.getAttr('object_type') === 'balloon') {

                                    // Update virtual balloon size before baking
                                    const current_size = obj.getAttr('balloon_size') || { x: 1, y: 1 };
                                    const new_size = {
                                        x: current_size.x * obj.scaleX(),
                                        y: current_size.y * obj.scaleY()
                                    };
                                    obj.setAttr('balloon_size', new_size);
                                    
                                    this.update_balloon_toolbar_position(obj);
                                    this.bake_balloon_transform(obj);
                                }

                                 
                            });
                        } 
                        else {
                            
                            // Fallback for single object if array is empty
                            if (obj.getAttr('object_type') === 'standalone_text' || obj.getAttr('object_type') === 'balloon') {
                                this.transformer.flipEnabled(false); // Disable flipping
                            }

                            if (object_type === 'balloon') {
                                // Update virtual balloon size before baking
                                const current_size = object.getAttr('balloon_size') || { x: 1, y: 1 };
                                const new_size = {
                                    x: current_size.x * object.scaleX(),
                                    y: current_size.y * object.scaleY()
                                };
                                object.setAttr('balloon_size', new_size);
                                
                                this.update_balloon_toolbar_position(object);
                                this.bake_balloon_transform(object);
                            }
                        }
                        
                        // Update UI values based on the primary transformed node
                        if (node && this.selected_object === node) {
                            if (node.getAttr('object_type') === 'balloon') {
                                // For balloons, use the virtual balloon_size property for UI
                                const balloon_size = node.getAttr('balloon_size') || { x: 1, y: 1 };
                                this.object_scale_value = balloon_size.x; // Assuming uniform scaling
                            } else {
                                this.object_scale_value = node.scaleX();
                            }
                            this.object_rotation_value = node.rotation();
                        }
                        
                        this._transform_update_pending = false;
                    });
                }
            });


            object.on('transformend.miko_trans', (e) => {
                // Final update of UI state after transform completes - Added based on previous correct logic
                this.update_ui_from_selected_object();
                //this.push_to_history('transform_object'); //moved to App.vue

                this.transformer.flipEnabled(true);

                //if type balloon
                if (object_type === 'balloon') {
                    this.update_balloon_toolbar_position(object);                    
                    this.show_transformer(object); 
                    this.redraw();
                }                
            });

            
        },

        
        deselect_object(restore_layer_visuals = true) {
            
            if (!this.selected_object) return;
            this.selected_object_array = []; // Clear the array reference

            //if type balloon
            if (this.selected_object.getAttr('object_type') === 'balloon') {
                this.selected_object_ref = this.selected_object;
            }

            if (this.text_editing.adjust_mode) {
                this.exit_text_adjust_mode();
            }
            
            // Just clear transformer, don't modify the object
            this.hide_transformer();
            this.exit_text_adjust_mode();
            this.selected_object = null;
            

            if (restore_layer_visuals) {
                this.selected_layer = null;
                this.selected_blend_mode = 'normal';
                this.selected_opacity = 100;
                this.selected_opacity_text = '100%';
            }           
            
            // Draw both layers
            this.redraw();
        },
        
        show_transformer(object, move_to_top = true, minimal = false)
        {                    
            if (!object || !this.transformer) return;          
            
            if (this.transformer.getLayer() !== this.ui_layer) {
                this.transformer.moveTo(this.ui_layer);
            }
            if (this.active_tool !== 'select' && this.active_tool !== "add_pointer" && this.active_tool !== 'select_panel') {
                this.hide_transformer();
                return;
            }

            // Check if we have multiple objects selected
            if (this.selected_object_array && this.selected_object_array.length > 1) {
                this.transformer.nodes([]);
                this.transformer.nodes(this.selected_object_array);
            } else if (object) {
                this.transformer.nodes([]);
                this.transformer.nodes([object]);
            } else {
                return; // No valid object to transform
            }

            this.transformer.borderStroke(null);
            this.transformer.anchorStroke(null);
            this.transformer.borderDash(null);
            
            // Use preference setting for default keepRatio behavior
            this.transformer.keepRatio(this.preferences.editor.transformer_keep_ratio); 
            
            if (minimal == false) {
                this.transformer.enabledAnchors([
                    'top-left', 'top-center', 'top-right',
                    'middle-left', 'middle-right',
                    'bottom-left', 'bottom-center', 'bottom-right'
                ]);

                this.transformer.rotateEnabled(true);
            }
            else {
                this.transformer.enabledAnchors([]); 
                this.transformer.rotateEnabled(false);
                this.transformer.borderEnabled(true);     
            }

            if (object.getAttr('object_type') === 'standalone_text' ) {
                this.transformer.borderStroke("#FF00FF");
                this.transformer.anchorStroke("#FF00FF");
                this.transformer.borderDash([4, 4]);              
                this.transformer.keepRatio(false); 
            }
              
            this.transformer.visible(true); 
                   
            if (move_to_top) {
                this.transformer.moveToTop();
            }

        },

        show_minimal_transformer(object) {
            this.show_transformer(object, true, true); 
        },

        hide_transformer() {
            if (!this.transformer) return;
            this.transformer.nodes([]);
            this.transformer.visible(false); 
        },

        delete_selected_object() {
            // Check if multiple objects are selected
            if (this.selected_object_array && this.selected_object_array.length > 1) {
                // Delete all selected objects
                const layers_to_delete = [];
                
                // Collect all layers corresponding to selected objects
                for (const selected_obj of this.selected_object_array) {
                    const layer = this.panel_layers.find(l => l.id === selected_obj.id());
                    if (layer && !layer.locked) {
                        layers_to_delete.push(layer);
                    }
                }
                
                // If no valid layers to delete, return early
                if (layers_to_delete.length === 0) return;
                
                // Set flag to prevent individual history entries
                this.is_performing_cut_cleanup = true;
                
                // Delete each layer without individual history entries
                for (const layer of layers_to_delete) {
                    this.delete_layer(layer);
                }
                
                // Reset flag and add single history entry for multiple deletion
                this.is_performing_cut_cleanup = false;
                this.push_to_history('delete_multiple_objects');
                
                // Clear selection array after deletion
                this.selected_object_array = [];
                this.deselect_object();
                
                return;
            }
            
            // Fallback for single object selection
            if (!this.selected_object) return;
            
            // Find the layer in the panel layers
            const layer = this.panel_layers.find(l => l.id === this.selected_object.id());
            if (!layer) return;
            
            // Call the delete_layer method with the found layer
            this.delete_layer(layer);
        },

        delete_selected_panel() {
            if (!this.selected_panel) return;
            
            // Cannot delete global panel
            if (this.selected_panel.getAttr('is_global') === true) return;
            
            // Exit panel edit mode if active
            if (this.panel_edit_mode_active && this.editing_panel === this.selected_panel) {
                this.exit_panel_edit_mode();
            }
            
            // Find panel in panels array
            const panel_index = this.panels.indexOf(this.selected_panel);
            if (panel_index === -1) return;
            
            // Remove panel from array
            this.panels.splice(panel_index, 1);
            
            // Destroy the panel (this will also destroy all its content)
            this.selected_panel.destroy();
            
            // Deselect panel and select global panel if available
            const global_panel = this.panels.find(panel => panel.getAttr('is_global'));
            if (global_panel) {
                this.selected_panel = global_panel;
            } else {
                this.selected_panel = null;
            }
            
            // Redraw
            this.redraw();
            
            // Record in history
            this.push_to_history('delete_panel');
        },

        move_panel_up() {
            if (!this.selected_panel) return;
            
            // Cannot move global panel
            if (this.selected_panel.getAttr('is_global') === true) return;
            
            // Get current index of the panel in the panels array
            const current_index = this.panels.indexOf(this.selected_panel);
            if (current_index === -1) return;
            
            // Find next non-global panel above current one
            let target_index = -1;
            for (let i = current_index + 1; i < this.panels.length; i++) {
                if (!this.panels[i].getAttr('is_global')) {
                    target_index = i;
                    break;
                }
            }
            
            // If found a target, swap positions
            if (target_index !== -1) {
                // Swap in array
                [this.panels[current_index], this.panels[target_index]] = [this.panels[target_index], this.panels[current_index]];
                
                // Update visual z-index (higher index = on top)
                this.selected_panel.moveUp();
                
                // Ensure global panel stays on top
                const global_panel = this.panels.find(panel => panel.getAttr('is_global'));
                if (global_panel) {
                    global_panel.moveToTop();
                }
                
                this.redraw();
                this.push_to_history('move_panel_up');
            }
        },

        move_panel_down() {
            if (!this.selected_panel) return;
            
            // Cannot move global panel
            if (this.selected_panel.getAttr('is_global') === true) return;
            
            // Get current index of the panel in the panels array
            const current_index = this.panels.indexOf(this.selected_panel);
            if (current_index === -1) return;
            
            // Find next non-global panel below current one
            let target_index = -1;
            for (let i = current_index - 1; i >= 0; i--) {
                if (!this.panels[i].getAttr('is_global')) {
                    target_index = i;
                    break;
                }
            }
            
            // If found a target, swap positions
            if (target_index !== -1) {
                // Swap in array
                [this.panels[current_index], this.panels[target_index]] = [this.panels[target_index], this.panels[current_index]];
                
                // Update visual z-index (lower index = behind)
                this.selected_panel.moveDown();
                
                this.redraw();
                this.push_to_history('move_panel_down');
            }
        },

        reset_object_scale() {
            if (!this.selected_object) return;
            
            if (this.selected_object.getAttr('object_type') === 'balloon') {
                // For balloons, reset to virtual size 1 and calculate needed transform
                const current_balloon_size = this.selected_object.getAttr('balloon_size') || { x: 1, y: 1 };
                const scale_factor_x = 1 / current_balloon_size.x;
                const scale_factor_y = 1 / current_balloon_size.y;
                
                // Apply the reset scale
                this.selected_object.scaleX(scale_factor_x);
                this.selected_object.scaleY(scale_factor_y);
                // Reset virtual size to 1
                this.selected_object.setAttr('balloon_size', { x: 1, y: 1 });
                
                // Bake the transform to maintain text and stroke consistency
                this.bake_balloon_transform(this.selected_object);
                
                // Recenter text after baking
                this.recenter_balloon_text(this.selected_object);

            } 
            else if (this.selected_object.getAttr('object_type') === 'standalone_text') {
                // For standalone text, reset scale to 1
                this.selected_object.scale({ x: 1, y: 1 });
                this.recenter_standalone_text(this.selected_object);
            } 
            else {
                // For images and other objects, direct scale reset
                this.selected_object.scale({ x: 1, y: 1 });
            }
            
            // Update transformer and redraw
            this.transformer.forceUpdate();
            
            // Update UI to reflect the reset
            this.update_ui_from_selected_object();
            
            this.redraw();
        },

        reset_object_rotation() {
            if (!this.selected_object) return;
            
            // Reset rotation to 0
            this.selected_object.rotation(0);
            
            // Update transformer and redraw
            this.transformer.forceUpdate();
            this.redraw();
        },
        
        rotate_object_with_slider() {
            if (!this.selected_object) return;
            if (this.performance_balancer) return;

            this.performance_balancer = true;

            requestAnimationFrame(() => {
                const new_rotation = this.object_rotation_value;
                this.selected_object.rotation(new_rotation);
                this.transformer.forceUpdate();
                this.redraw();

                this.performance_balancer = false;
            });                        
        },

        resize_object_with_slider() {
            if (!this.selected_object) return;
            if (this.performance_balancer) return;

            this.performance_balancer = true;
            requestAnimationFrame(() => {
                if (this.selected_object.getAttr('object_type') === 'balloon') {
                    // For balloons, calculate the scale needed to achieve the desired virtual size
                    const current_balloon_size = this.selected_object.getAttr('balloon_size') || { x: 1, y: 1 };
                    const scale_factor_x = this.object_scale_value / current_balloon_size.x;
                    const scale_factor_y = this.object_scale_value / current_balloon_size.y;
                    
                    // Apply the scale
                    this.selected_object.scaleX(scale_factor_x);
                    this.selected_object.scaleY(scale_factor_y);
                    
                    // Update virtual size
                    this.selected_object.setAttr('balloon_size', { 
                        x: this.object_scale_value, 
                        y: this.object_scale_value                    });
                    
                    // Bake the transform to maintain text and stroke consistency
                    this.bake_balloon_transform(this.selected_object);
                    
                    // Recenter text after baking
                    this.recenter_balloon_text(this.selected_object);
                } 
                else if (this.selected_object.getAttr('object_type') === 'standalone_text') {
                    // For standalone text, apply scale to the group
                    this.selected_object.scaleX(this.object_scale_value);
                    this.selected_object.scaleY(this.object_scale_value);
                    this.recenter_standalone_text(this.selected_object);
                }
                
                else {
                    // For images and other objects, use normal scaling
                    const new_scale = {
                        x: this.object_scale_value,
                        y: this.object_scale_value 
                    };
                    this.selected_object.scale(new_scale);
                }
                
                this.transformer.forceUpdate();
                this.redraw();                 
                this.performance_balancer = false;
            });
  
        },

        resize_selected_object(scale_factor) {
            if (!this.selected_object) return;
            
            // Get current scale
            const current_scale = {
                x: this.selected_object.scaleX(),
                y: this.selected_object.scaleY()
            };
            
            // Apply new scale while keeping aspect ratio
            const new_scale = {
                x: current_scale.x * scale_factor,
                y: current_scale.y * scale_factor
            };
            
            // Apply the new scale
            this.selected_object.scale(new_scale);
            
            // Update transformer and redraw
            this.transformer.forceUpdate();
            this.stage.batchDraw();
            //this.redraw();
            
            // Don't record every small change in history to avoid flooding
            // Use a debounced approach with a timeout
            if (this.resize_timer) {
                clearTimeout(this.resize_timer);
            }
            
            this.resize_timer = setTimeout(() => {
                this.push_to_history('resize_object');
                this.resize_timer = null;
            }, 500); // Record after 300ms of inactivity
        },

        // Check if the selected object is an image
        is_selected_object_image() {
            if (!this.selected_object) return false;
            return this.selected_object.getAttr('object_type') === 'image';
        },

        // Image manipulation methods
        flip_selected_image_horizontal() {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'image') {
                this.status_message = this.get_t('status.no_image_selected');
                return;
            }
            
            // Flip by inverting the scaleX
            const current_scale_x = this.selected_object.scaleX();
            this.selected_object.scaleX(-current_scale_x);
            
            // Update transformer and redraw
            this.transformer.forceUpdate();
            this.redraw();
            
            // Record in history
            this.push_to_history('flip_image_horizontal');
        },

        flip_selected_image_vertical() {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'image') {
                this.status_message = this.get_t('status.no_image_selected');
                return;
            }
            
            // Flip by inverting the scaleY
            const current_scale_y = this.selected_object.scaleY();
            this.selected_object.scaleY(-current_scale_y);
            
            // Update transformer and redraw
            this.transformer.forceUpdate();
            this.redraw();
            
            // Record in history
            this.push_to_history('flip_image_vertical');
        },

        show_object_specific_controls(object_type) {
            this.show_image_controls = false;
            this.show_balloon_controls = false;
            
            switch (object_type) {
                case 'image':
                    this.show_image_controls = true;
                break;
                case 'balloon':
                    this.show_balloon_controls = true;
                break;
            }
        },
        
        async load_image_into_panel() {
            this.deselect_object();

            if (!this.selected_panel) {
                // Handle global image case
                return;
            }

            // Check premium limit for layers
            const current_layer_count = this.get_panel_layer_count(this.selected_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }

            const panel = this.selected_panel;
            // Create file input
            const file_input = document.createElement('input');
            file_input.type = 'file';
            file_input.accept = 'image/*';
            
            file_input.onchange = (e) => {
                const file = e.target.files[0];
                if (!file) return;
                
                const reader = new FileReader();
                reader.onload = (event) => {
                    this.create_image_in_panel(event.target.result, panel);
                };
                
                reader.readAsDataURL(file);
            };
            
            file_input.click();
        },

        async paste_image_from_clipboard() {
            if (!this.selected_panel) {
                this.show_notification_modal(this.get_t('canvas.no_panel_selected'), 'warning');
                return;
            }

            // Check premium limit for layers
            const current_layer_count = this.get_panel_layer_count(this.selected_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }

            try {
                const clipboard_items = await navigator.clipboard.read();
                let image_found = false;

                for (const clipboard_item of clipboard_items) {
                    for (const type of clipboard_item.types) {
                        if (type.startsWith('image/')) {
                            const blob = await clipboard_item.getType(type);
                            const reader = new FileReader();
                            
                            reader.onload = (event) => {
                                this.create_image_in_panel(event.target.result, this.selected_panel, true);
                            };
                            
                            reader.readAsDataURL(blob);
                            image_found = true;
                            break;
                        }
                    }
                    if (image_found) break;
                }

                if (!image_found) {
                    this.show_notification_modal(this.get_t('canvas.no_image_in_clipboard'), 'warning');
                } else {
                    // Clear external clipboard after successful image paste
                    this.clear_external_clipboard();
                }
            } catch (error) {
                console.error('Error accessing clipboard:', error);
                this.show_notification_modal(this.get_t('canvas.clipboard_access_error'), 'error');
            }
        },

        create_image_in_panel(image_data, panel, from_clipboard = false) {
            // Create image object
            const image = new Image();
            
            // Configure CORS for R2/external images to avoid tainted canvas
            // if (image_data.startsWith('http')) {
            //     image.crossOrigin = 'anonymous';
            // }
            
            image.onload = () => {
                // Calculate polygon bounds for panel dimensions
                const bounds = this.calculate_polygon_bounds(panel.points);
                const content_width = Math.max(bounds.width, 50);
                const content_height = Math.max(bounds.height, 50);
                const center_x = bounds.min_x + bounds.width / 2;
                const center_y = bounds.min_y + bounds.height / 2;
                
                // Calculate scale to fit image properly
                const scale_x = Math.max(content_width / image.width, 0.01);
                const scale_y = Math.max(content_height / image.height, 0.01);
                const scale = Math.min(scale_x, scale_y, 1);
                
                // Use the unified function for layer name
                const layer_name = this.get_next_layer_name(panel);
                
                // Create Konva image
                const konva_image_raw = new Konva.Image({
                    image: image,
                    x: center_x,
                    y: center_y,
                    offset: {
                        x: image.width / 2,
                        y: image.height / 2
                    },
                    scaleX: scale,
                    scaleY: scale,
                    draggable: this.active_tool === 'select',
                    name: 'panel_image',
                    id: 'image_' + Date.now(),
                    listening: true,
                    globalCompositeOperation: 'normal',
                    opacity: 1
                });

                const konva_image = markRaw(konva_image_raw);

                konva_image.setAttr('src_data', image_data);
                konva_image.setAttr('layer_name', layer_name);
                konva_image.setAttr('locked', false);
                konva_image.setAttr('object_type', 'image');
                
                // Add image to panel content group
                panel.content_group.add(konva_image);
                
                // Select the newly added image
                if (this.active_tool === 'select') {
                    this.select_object(konva_image);
                }
                

                // Create layer object
                this.selected_layer = {
                    id: konva_image.id(),
                    name: layer_name,
                    node: konva_image,
                    visible: true,
                    locked: false
                };
                
                // If image was pasted from clipboard, add to internal clipboard
                if (from_clipboard) {
                    // Create object data for internal clipboard
                    const object_data = {
                        id: konva_image.id(),
                        object_type: 'image',
                        layer_name: layer_name,
                        x: center_x,
                        y: center_y,
                        scaleX: scale,
                        scaleY: scale,
                        rotation: 0,
                        offsetX: image.width / 2,
                        offsetY: image.height / 2,
                        opacity: 1,
                        globalCompositeOperation: 'normal',
                        src_data: image_data,
                        locked: false
                    };
                    
                    this.clipboard_object_data = object_data;
                    this.clipboard_multi_object_data = [object_data];
                }
                
                // Draw the layer and record in history
                this.panels_layer.draw();
                this.push_to_history('add_image');
            };
            
            image.src = image_data;
        },

        calculate_polygon_bounds(points) {
            let min_x = Infinity;
            let min_y = Infinity;
            let max_x = -Infinity;
            let max_y = -Infinity;
            
            // Process points in pairs (x,y)
            for (let i = 0; i < points.length; i += 2) {
                const x = points[i];
                const y = points[i+1];
                
                min_x = Math.min(min_x, x);
                min_y = Math.min(min_y, y);
                max_x = Math.max(max_x, x);
                max_y = Math.max(max_y, y);
            }
            
            return {
                min_x,
                min_y,
                max_x,
                max_y,
                width: max_x - min_x,
                height: max_y - min_y
            };
        },
        update_outline_width(value) {
            this.outline_width = parseInt(value, 10);
            this.panels.forEach(panel => {
                // Skip global panel
                if (panel.getAttr('is_global') === true) return;
                if (panel.outline) {
                    panel.outline.strokeWidth(this.outline_width);
                }
            });
            this.panels_layer.batchDraw();
        },
        
        update_outline_color(color) {
            if (!this.stage) return;
            
            // Update the outline color property
            this.outline_color = color;
            
            this.panels.forEach(panel => {
                // Skip global panel
                if (panel.getAttr('is_global') === true) return;
                if (panel.outline) {
                    panel.outline.stroke(color);
                }
                // Also update the selected panel highlight if necessary
                if (this.selected_panel === panel) {
                    // Keep highlight color distinct or update based on new logic if needed
                    // For now, let's assume highlight remains blue
                    // panel.outline.stroke('#0096ff');
                }
            });
            this.panels_layer.batchDraw();
        },
        
        update_page_margin() { 

            this.page_margin = parseInt(this.page_margin, 10);
            if (!this.panels.length) return;

            var reference_panel = null;

            if (this.panels[0].getAttr('is_global')){
                reference_panel = this.panels[1];
            }
            else {
                reference_panel = this.panels[0];
            }

            this.exit_panel_edit_mode(); // Exit any panel edit mode before resizing
    
            // Calculate current and new content area dimensions
            const current_margin = reference_panel.x(); // Current margin (assuming it's at margin position)
            const current_content_width = this.canvas_width - (2 * current_margin);
            const current_content_height = this.canvas_height - (2 * current_margin);
            const new_content_width = this.canvas_width - (2 * this.page_margin);
            const new_content_height = this.canvas_height - (2 * this.page_margin);
            
            // Scale factors for x and y dimensions
            const scale_x = new_content_width / current_content_width;
            const scale_y = new_content_height / current_content_height;
            
            // Update each panel with proper scaling of points and relative positioning
            this.panels.forEach(panel => {

                if (panel.getAttr('is_global') === true) return;
                
                // Only update panels that respect margins
                if (!panel.getAttr('respect_margins')) return;

                // Calculate relative position within current content area
                const relative_x = (panel.x() - current_margin) / current_content_width;
                const relative_y = (panel.y() - current_margin) / current_content_height;
                
                // Calculate new position maintaining relative placement
                const new_panel_x = this.page_margin + (relative_x * new_content_width);
                const new_panel_y = this.page_margin + (relative_y * new_content_height);
                
                // Move panel to new relative position
                panel.x(new_panel_x);
                panel.y(new_panel_y);
                // Scale all polygon points to match new dimensions
                const new_points = [];
                for (let i = 0; i < panel.points.length; i += 2) {
                    // Scale x coordinate
                    new_points[i] = panel.points[i] * scale_x;
                    // Scale y coordinate
                    new_points[i + 1] = panel.points[i + 1] * scale_y;
                }
                
                // Update all elements with new scaled points
                panel.points = new_points;
                panel.background.points(new_points);
                panel.outline.points(new_points);
                
                // Update clip function with new points
                panel.content_group.clipFunc(function(ctx) {
                    ctx.beginPath();
                    for (let i = 0; i < new_points.length; i += 2) {
                        if (i === 0) ctx.moveTo(new_points[i], new_points[i + 1]);
                        else ctx.lineTo(new_points[i], new_points[i + 1]);
                    }
                    ctx.closePath();
                });
                
                // Scale position of objects within the panel
                const images = panel.content_group.find('.panel_image');
                images.forEach(img => {
                    img.x(img.x() * scale_x);
                    img.y(img.y() * scale_y);
                });
                
                // Scale position of balloons within the panel
                const balloons = panel.content_group.find('.panel_balloon');
                balloons.forEach(balloon => {
                    balloon.x(balloon.x() * scale_x);
                    balloon.y(balloon.y() * scale_y);
                });
                
                // Scale position of standalone texts within the panel
                const standalone_texts = panel.content_group.find('.standalone_text');
                standalone_texts.forEach(text => {
                    text.x(text.x() * scale_x);
                    text.y(text.y() * scale_y);
                });
            });
            
            // Redraw layers
            this.redraw();
             
        },
        
        handle_window_resize(disable_auto_fit = false) {
            if (!this.stage) return;
            
            // Get updated container dimensions
            const container = document.getElementById('canvas_wrapper');
            const container_width = container.offsetWidth;
            const container_height = container.offsetHeight;
            
            // Resize stage to match container
            this.stage.width(container_width);
            this.stage.height(container_height);
            
            // Reapply auto-fit if needed
            if (this.initial_auto_zoom && !disable_auto_fit) {
                this.auto_fit_canvas_to_screen();
            }

            if (this.selected_object_is_balloon)
            {
                this.update_balloon_toolbar_position(this.selected_object);
            }
            
        },

        adjust_zoom(factor, center_point = null, smooth = false) {
            const old_zoom = this.canvas_zoom;
            let new_zoom = old_zoom * factor;
            
            // Apply zoom limits
            new_zoom = Math.max(this.min_zoom, Math.min(this.max_zoom, new_zoom));
            
            this.set_zoom_level(new_zoom, center_point, smooth);
        },
        
        // Set zoom to a specific level with optional smooth animation
        set_zoom_level(zoom_level, center_point = null, smooth = false) {
            if (!this.stage) return;
            
            // Apply zoom limits
            zoom_level = Math.max(this.min_zoom, Math.min(this.max_zoom, zoom_level));
            
            const old_zoom = this.canvas_zoom;
            
            // Don't do anything if the zoom level hasn't changed
            if (Math.abs(old_zoom - zoom_level) < 0.001) return;
            
            // Get container and stage dimensions
            const container = this.stage.container();
            
            // Calculate the center point if not provided
            if (!center_point) {
                center_point = {
                    x: container.offsetWidth / 2,
                    y: container.offsetHeight / 2
                };
            }
            
            // Calculate new position to keep zoom centered on mouse position
            const mouse_point_to = {
                x: (center_point.x - this.stage.x()) / old_zoom,
                y: (center_point.y - this.stage.y()) / old_zoom
            };
            
            if (smooth) {
                // Animated zoom
                this.animate_zoom(old_zoom, zoom_level, center_point, mouse_point_to);
            } else {
                // Immediate zoom
                this.canvas_zoom = zoom_level;
                
                const new_pos = {
                    x: center_point.x - mouse_point_to.x * zoom_level,
                    y: center_point.y - mouse_point_to.y * zoom_level
                };
                
                // Apply the zoom using the scale property
                this.stage.scale({ x: zoom_level, y: zoom_level });
                
                // Update stage position
                this.stage.position(new_pos);
                this.stage.batchDraw();
            }
        },
        
        // Animate zoom smoothly between two levels
        animate_zoom(start_zoom, end_zoom, center_point, mouse_point_to) {
            const start_time = Date.now();
            const duration = this.zoom_animation_speed * 1000; // Convert to milliseconds
            
            // Animation frame function
            const animate = () => {
                const now = Date.now();
                let progress = (now - start_time) / duration;
                
                // Clamp progress
                progress = Math.min(1, Math.max(0, progress));
                
                // Ease function (ease out cubic)
                const ease = 1 - Math.pow(1 - progress, 3);
                
                // Calculate current zoom
                const current_zoom = start_zoom + (end_zoom - start_zoom) * ease;
                this.canvas_zoom = current_zoom;
                
                // Calculate new position
                const new_pos = {
                    x: center_point.x - mouse_point_to.x * current_zoom,
                    y: center_point.y - mouse_point_to.y * current_zoom
                };
                
                // Apply zoom and position
                this.stage.scale({ x: current_zoom, y: current_zoom });
                this.stage.position(new_pos);
                this.stage.batchDraw();
                
                // Continue animation if not completed
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            
            // Start animation
            requestAnimationFrame(animate);
        },
        
        reset_zoom_to_100_percent() {
            const container = this.stage.container();
            const center_point = {
                x: container.offsetWidth / 2,
                y: container.offsetHeight / 2
            };
            
            // Reset to 100% with smooth animation
            this.set_zoom_level(1, center_point, true);
        },
        
        // Auto-fit canvas to screen on initial load
        auto_fit_canvas_to_screen() {
            if (!this.stage) return;

            const container = this.stage.container();
            
            // Get available dimensions
            const available_width = container.offsetWidth;
            const available_height = container.offsetHeight;
            
            // Calculate margin
            const margin = (this.zoom_margin_percent / 100) * Math.min(this.canvas_width, this.canvas_height);
            
            // Calculate scale factors with margin
            const scale_x = (available_width - margin*2) / this.canvas_width;
            const scale_y = (available_height - margin*2) / this.canvas_height;
            
            // Use the smaller scale to ensure entire canvas is visible
            const scale = Math.min(scale_x, scale_y);
            
            // Only auto-zoom if needed (scale < 1 meaning we need to zoom out)
            // or if we specifically want to enforce auto zoom
            if (scale < 1 || this.initial_auto_zoom) {
                // Set zoom level but limit to 100% maximum on initial load
                const zoom_level = Math.min(scale, 1);
                this.set_zoom_level(zoom_level);
                
                // Center the canvas
                const center_x = (available_width - this.canvas_width * zoom_level) / 2;
                const center_y = (available_height - this.canvas_height * zoom_level) / 2;
                
                this.stage.position({ x: center_x, y: center_y });
                this.stage.batchDraw();
            }
        },
        
        // Get current zoom as percentage for display
        get_current_zoom() {
            return Math.round(this.canvas_zoom * 100);
        },

        update_background_color(color) {
            if (!this.stage) return;
            
            // Update the background color property
            this.background_color = color;
            
            // Update background color of all relevant elements
            const background = this.panels_layer.findOne('Rect');
            if (background) {
                background.fill(color);
            }
            
            // this.panels.forEach(panel => {
            //     panel.outer.fill(this.background_color);
            // });
            
            this.panels_layer.draw();
        },
        
        // Add a helper method to update the transformer's position considering panel transforms
        // update_transformer() {
        //     if (!this.selected_object) return;
            
        //     // Ensure transformer is attached to the selected object
        //     this.show_transformer(this.selected_object, false);
            
        //     // Force update and redraw
        //     this.transformer.forceUpdate();
        //     this.redraw();
        // },

        // Add helper method to find panel containing an object
        find_parent_panel_for_object(object) {
            if (!object) return null;
            // Search all panels for the object in their content_group
            for (const panel of this.panels) {
                if (panel.content_group && panel.content_group.findOne('#' + object.id())) {
                    return panel;
                }
            }

            return null;
        },

        // Nueva función para convertir coordenadas del puntero teniendo en cuenta zoom y pan
        get_pointer_position() {
            if (!this.stage) return null;
            
            // Obtener posición bruta del puntero
            const pointer_pos = this.stage.getPointerPosition();
            if (!pointer_pos) return null;
            
            // Ajustar según la escala y posición del stage
            return {
                x: (pointer_pos.x - this.stage.x()) / this.canvas_zoom,
                y: (pointer_pos.y - this.stage.y()) / this.canvas_zoom
            };
        },


        /**
         * Get the center point of the selected panel
         */
        get_panel_center(panel = null) {
            let target_panel = panel || this.selected_panel;
            
            // If no panel provided or selected, use global panel
            if (!target_panel || !target_panel.points || target_panel.points.length === 0) {
                target_panel = this.panels.find(panel => panel.getAttr('is_global'));
                if (!target_panel || !target_panel.points || target_panel.points.length === 0) {
                    // If global panel doesn't exist or has no points, return canvas center
                    return {
                        x: this.canvas_width / 2,
                        y: this.canvas_height / 2
                    };
                }
            }
            
            const bounds = this.get_panel_bounds(target_panel);
            const panel_x = target_panel.x();
            const panel_y = target_panel.y();
            
            return {
                x: panel_x + bounds.minX + bounds.width / 2,
                y: panel_y + bounds.minY + bounds.height / 2
            };
        },

        // Add a new method for zoom in
        zoom_in() {
            const container = this.stage.container();
            const center_point = {
                x: container.offsetWidth / 2,
                y: container.offsetHeight / 2
            };
            this.adjust_zoom(1.2, center_point, true);
        },
        
        // Add a new method for zoom out
        zoom_out() {
            const container = this.stage.container();
            const center_point = {
                x: container.offsetWidth / 2,
                y: container.offsetHeight / 2
            };
            this.adjust_zoom(0.8, center_point, true);
        },
        
        // Add a new method to reset both position and zoom (auto-fit)
        reset_position_and_zoom() {
            this.auto_fit_canvas_to_screen();
        },

        update_new_doc_preview() {
            const preview_area = document.getElementById('preview_area');
            const preview_canvas = document.getElementById('preview_canvas');
            const preview_margin_box = document.getElementById('preview_margin_box');
            // const preview_content_box = document.getElementById('preview_content_box'); // Not strictly needed for styling

            if (!preview_area || !preview_canvas || !preview_margin_box) return;

            let preview_w, preview_h;
            const aspect_ratio_id = this.new_doc_options.aspect_ratio_id;

            // Determine dimensions based on selected aspect ratio
            if (aspect_ratio_id === 'custom') {
                preview_w = this.new_doc_options.custom_width || 800;
                preview_h = this.new_doc_options.custom_height || 1200;
            } else {
                const dimensions = this.calculate_dimensions_from_aspect_ratio(
                    aspect_ratio_id, 
                    this.new_doc_options.base_size
                );
                preview_w = dimensions.width;
                preview_h = dimensions.height;
            }

            const aspect_ratio = preview_w / preview_h;
            const container_w = preview_area.offsetWidth;
            const container_h = preview_area.offsetHeight;

            // Calculate scaled dimensions for the preview canvas to fit container
            let scaled_w, scaled_h;
            if (container_w / container_h > aspect_ratio) {
                // Fit to height
                scaled_h = container_h;
                scaled_w = container_h * aspect_ratio;
            } else {
                // Fit to width
                scaled_w = container_w;
                scaled_h = container_w / aspect_ratio;
            }

            // Apply size and background to preview canvas
            preview_canvas.style.width = `${scaled_w}px`;
            preview_canvas.style.height = `${scaled_h}px`;
            preview_canvas.style.backgroundColor = this.new_doc_options.background_color; // Correct background
            preview_canvas.style.borderColor = this.new_doc_options.outline_color; // Use outline color for canvas border
            preview_canvas.style.borderStyle = 'solid';
            preview_canvas.style.borderWidth = '1px'; // Thin border for the canvas itself

            // Center the canvas
            preview_canvas.style.left = `${(container_w - scaled_w) / 2}px`;
            preview_canvas.style.top = `${(container_h - scaled_h) / 2}px`;

            // Calculate relative margin and outline for the preview scale
            const margin_percent_w = (this.new_doc_options.page_margin / preview_w) * 100;
            const margin_percent_h = (this.new_doc_options.page_margin / preview_h) * 100;
            const outline_px = (this.new_doc_options.outline_width / preview_w) * scaled_w; // Approximate scaled outline

            // Apply styles to margin box (represents the area inside margins with the outline)
            preview_margin_box.style.left = `${margin_percent_w}%`;
            preview_margin_box.style.top = `${margin_percent_h}%`;
            preview_margin_box.style.right = `${margin_percent_w}%`;
            preview_margin_box.style.bottom = `${margin_percent_h}%`;
            preview_margin_box.style.borderWidth = `${Math.max(outline_px, 0)}px`;
            preview_margin_box.style.borderColor = this.new_doc_options.outline_color; // Use outline color
            preview_margin_box.style.borderStyle = 'solid';
        },

        //Creates a new document using the options gathered from the dialog.
        create_new_document_with_options(add_page = false) {
            if (!this.stage) return;

            // Reset file handle only for new documents (not for adding pages)
            if (!add_page) {
                this.current_file_handle = null;
                this.current_file_path = '';
                this.autosave_original_filename = null;
            }

            let target_width, target_height;
            const aspect_ratio_id = this.new_doc_options.aspect_ratio_id;

            // Determine target dimensions
            if (aspect_ratio_id === 'custom') {
                // For premium users, allow higher limits (up to 2500px for performance)
                const max_dim = this.user_data.is_premium ? 2500 : 1280;
                target_width = Math.max(800, Math.min(max_dim, this.new_doc_options.custom_width || 800));
                target_height = Math.max(800, Math.min(max_dim, this.new_doc_options.custom_height || 1200));
            } else {
                const dimensions = this.calculate_dimensions_from_aspect_ratio(
                    aspect_ratio_id, 
                    this.new_doc_options.base_size
                );
                target_width = dimensions.width;
                target_height = dimensions.height;
            }

            // --- Apply the new settings ---
            // 1. Update core properties
            this.canvas_width = target_width;
            this.canvas_height = target_height;
            this.current_aspect_ratio = aspect_ratio_id;
            this.page_margin = this.new_doc_options.page_margin;
            this.outline_width = this.new_doc_options.outline_width;
            this.outline_color = this.new_doc_options.outline_color;
            this.background_color = this.new_doc_options.background_color;
            
            if (!add_page) {
                // Only for new documents
                

                // Initialize MKC2 container for all new documents
                this.initialize_mkc2_structure();
            }

            // 2. Update the background rectangle size and color
            const background = this.panels_layer.findOne('.background');
            if (background) {
                background.width(this.canvas_width);
                background.height(this.canvas_height);
                background.fill(this.background_color);
            } else {
                 const new_background = new Konva.Rect({
                    x: 0, y: 0,
                    width: this.canvas_width, height: this.canvas_height,
                    fill: this.background_color, name: 'background'
                });
                this.panels_layer.add(new_background);
                new_background.moveToBottom();
            }

            // Update canvas clipping bounds
            this.update_canvas_clipping();

            // 3. Reset the layout (will now use the updated outline_color)
            // For new documents, preserve MKC2 structure; for adding pages, use normal reset
            this.reset_layout(!add_page);

            // 4. Hide welcome screen first
            this.hide_welcome_screen_view();

            // 5. Wait for DOM update then adjust zoom
            this.$nextTick(() => {
                this.auto_fit_canvas_to_screen();
            });

            // 6. Redraw layers
            this.redraw();

            // 7. Clear history
            this.history_stack = this.history_stack.slice(-1);
            this.redo_stack = [];

            //Select global panel
            this.selected_panel = this.panels.find(panel => panel.getAttr('is_global'));

            // 8. Generate thumbnail for ALL pages (new documents and new pages)
            this.$nextTick(async () => {
                try {
                    const thumbnail_base64 = await this.generate_document_thumbnail_base64();
                    
                    if (thumbnail_base64 && this.container_data && this.container_data.pages[this.current_page_index]) {
                        this.container_data.pages[this.current_page_index].thumbnail = thumbnail_base64;
                        
                        // If this is page 1 (index 0), sync container thumbnail for compatibility
                        if (this.current_page_index === 0) {
                            this.document_metadata = this.document_metadata || {};
                            this.document_metadata.document_thumbnail = thumbnail_base64;
                        }
                    }
                } catch (error) {
                    console.error('Error generating page thumbnail:', error);
                }
                
                // Don't mark as unsaved when adding new page - let the user make actual changes first
            });

            console.log(add_page ? 'New page created with current settings.' : 'New document created with specified options.');
        },
        
        rgb_to_hex(r, g, b) {
            // Ensure values are within valid range (0-255)
            r = Math.min(255, Math.max(0, Math.round(r)));
            g = Math.min(255, Math.max(0, Math.round(g)));
            b = Math.min(255, Math.max(0, Math.round(b)));
            
            // Convert to hex and pad with zeros if needed
            return '#' + 
                (r < 16 ? '0' : '') + r.toString(16) + 
                (g < 16 ? '0' : '') + g.toString(16) + 
                (b < 16 ? '0' : '') + b.toString(16);
        },
        // Helper method to update UI controls from the selected object's properties
        update_ui_from_selected_object() {
            if (!this.selected_object) {
                // Reset values if no object is selected
                this.object_scale_value = 1;
                this.object_rotation_value = 0;
                this.selected_blend_mode = 'normal';
                this.selected_opacity = 100;
                this.selected_opacity_text = '100%';
                // Stroke
                this.selected_object_shape_stroke_width = this.preferences.balloon_defaults.stroke_width;
                this.selected_object_stroke_dash = '';
                return;
            }
            
            // Directly read properties from the raw Konva object
            if (this.selected_object.getAttr('object_type') === 'balloon') {
                // For balloons, use the virtual balloon_size property
                const balloon_size = this.selected_object.getAttr('balloon_size') || { x: 1, y: 1 };
                this.object_scale_value = balloon_size.x; // Assuming uniform scaling
                  // Update stroke width from selected object - respect 0 values
                const original_stroke_width = this.selected_object.getAttr('original_stroke_width');
                const stroke_width = (original_stroke_width !== undefined && original_stroke_width !== null) ? original_stroke_width : this.preferences.balloon_defaults.stroke_width;
                this.selected_object_shape_stroke_width = stroke_width;
                
                // Update stroke dash from selected object
                const stroke_dash = this.selected_object.getAttr('original_stroke_dash') || '';
                this.selected_object_stroke_dash = stroke_dash;
            } else {
                // For images and other objects, use actual scale
                this.object_scale_value = this.selected_object.scaleX();
            }
            this.object_rotation_value = this.selected_object.rotation();

            // Update blend mode and opacity using the existing method
            this.set_object_blend_mode_and_opacity(this.selected_object);
            
            // Update text editing interface if text editing is active and we're selecting a text object
            if (this.text_editing && this.text_editing.active) {
                const object_type = this.selected_object.getAttr('object_type');
                if (object_type === 'balloon' || object_type === 'standalone_text') {
                    const text_node = this.get_active_balloon_text_node();
                    if (text_node) {
                        const default_text_color = this.preferences.text_defaults.text_color;
                        const default_text_stroke = this.preferences.text_defaults.text_stroke;

                        this.text_editing.text = text_node.text();
                        this.text_editing.font_size = text_node.fontSize();
                        this.text_editing.font_family = text_node.fontFamily();
                        this.text_editing.align = text_node.align();
                        this.text_editing.text_color = text_node.fill() || default_text_color;
                        this.text_editing.text_stroke = text_node.stroke() || default_text_stroke;
                        this.text_editing.text_stroke_width = text_node.strokeWidth();
                        this.text_editing.line_height = text_node.lineHeight() || 1;

                        const style = (text_node.fontStyle && text_node.fontStyle()) || '';
                        this.text_editing.is_bold = style.includes('bold');
                        this.text_editing.is_italic = style.includes('italic');
                    }
                }
            }
        },

        // Override change_tool to handle exiting balloon edit mode
        change_tool(tool_name) {
            if (this.active_tool === tool_name) return; // No change

            // If currently editing a balloon and switching to a tool other than 'select', exit edit mode
            if (this.balloon_edit_mode_active && tool_name !== 'select') {
                console.log(`Changing tool to ${tool_name} while in balloon edit mode. Exiting edit mode.`);
                this.exit_balloon_edit_mode();
            }

            this.exit_panel_edit_mode();

            // Call the original change_tool logic (assuming it exists in another mixin or the main component)
            // This might need adjustment based on where the original change_tool is defined.
            // If change_tool is defined in this mixin itself, just proceed:
            this.active_tool = tool_name;
            this.status_message = `Tool changed to ${tool_name}`;

            // Deselect object if switching away from select tool
            if (tool_name !== 'select' && this.selected_object) {
                this.deselect_object();
            }

            // Reset subtool when changing main tool
            this.deselect_subtool();

            // Update cursor based on the new tool
            this.update_cursor();

            this.redraw(); // Redraw needed if cursor/UI changes
        },
        
        /**
         * Copies the currently selected object(s) to the clipboard_object_data.
         * When multiple objects are selected (in selected_object_array), all will be copied.
         */
        copy_selected_object() {
            if (!this.selected_object) {
                this.status_message = this.get_t('status.no_object_selected');
                console.error("Copy failed: No object selected.");
                return; 
            }
            
            // Clear external clipboard to ensure internal clipboard takes priority
            this.clear_external_clipboard();
            
            // Check if we have multiple objects selected
            const has_multiple_objects = this.selected_object_array && this.selected_object_array.length > 1;
            
            // Clear multi-object clipboard
            this.clipboard_multi_object_data = [];
            
            // Get panel for either single or multiple object cases
            let panel;
            if (!has_multiple_objects) {
                // Single object copy path (original behavior)
                panel = this.find_parent_panel_for_object(this.selected_object);            
                if (!panel) {
                    this.status_message = this.get_t('status.panel_not_found_for_selected_object');
                    console.error("Copy failed: Could not find parent panel for object ID:", this.selected_object.id());
                    return;
                }
            } else {
                // For multi-select, use the panel of the first/primary selected object
                panel = this.find_parent_panel_for_object(this.selected_object);
                if (!panel) {
                    this.status_message = this.get_t('status.panel_not_found_for_primary_selected_object');
                    console.error("Copy failed: Could not find parent panel for primary object");
                    return;
                }
            }
        
            // Ensure panel ID is retrieved correctly
            const panel_id = typeof panel.id === 'function' ? panel.id() : panel.id;
            if (!panel_id) {
                this.status_message = this.get_t('status.panel_id_missing');
                console.error("Copy failed: Parent panel is missing an ID."); 
                return;
            }
        
            // Use the current snapshot system
            const snapshot = this.create_snapshot(); 
            if (!snapshot || !Array.isArray(snapshot.panels_data)) {
                this.status_message = this.get_t('status.failed_to_create_snapshot');
                console.error("Copy failed: create_snapshot did not return valid data.");
                return;
            }
        
            // Find panel_data by comparing with panel's id
            const panel_data = snapshot.panels_data.find(p => p && p.id === panel_id); 
        
            if (!panel_data) {
                this.status_message = this.get_t('status.panel_data_not_found_in_snapshot');
                // Mostrar información adicional para depuración
                console.error(`Copy failed: Panel data for ID '${panel_id}' not found in snapshot. Available IDs:`, snapshot.panels_data.map(p => p ? p.id : 'null/undefined'));
                return;
            }
        
            // Verificar si la propiedad is_global tiene valores consistentes
            const is_global_attr = panel.getAttr('is_global');
            const is_global_snapshot = panel_data.is_global;
            
            // Log para depuración
            //console.log(`Panel ${panel_id} - is_global attribute: ${is_global_attr}, is_global in snapshot: ${is_global_snapshot}`);
            
            // Explicitly check if content array exists and is an array
            if (!Array.isArray(panel_data.content)) {
                this.status_message = this.get_t('status.panel_content_data_missing');
                console.error(`Copy failed: Panel data found for ID '${panel_id}', but 'content' is not an array. Panel data:`, panel_data);
                return;
            }            // Check if we have multiple objects or just one
            if (this.selected_object_array && this.selected_object_array.length > 1) {
                // Multi-object copy path
                for (const selected_obj of this.selected_object_array) {
                    const obj_panel = this.find_parent_panel_for_object(selected_obj);
                    if (!obj_panel) continue; // Skip objects without panels
                    
                    const obj_panel_id = typeof obj_panel.id === 'function' ? obj_panel.id() : obj_panel.id;
                    const obj_panel_data = snapshot.panels_data.find(p => p && p.id === obj_panel_id);
                    
                    if (!obj_panel_data || !Array.isArray(obj_panel_data.content)) continue;
                    
                    const obj_id = typeof selected_obj.id === 'function' ? selected_obj.id() : selected_obj.id;
                    if (!obj_id) continue;
                    
                    const obj_data = obj_panel_data.content.find(obj => obj && obj.id === obj_id);
                    if (obj_data) {
                        // Add to multi-object clipboard
                        try {
                            const copied_data = JSON.parse(JSON.stringify(obj_data));
                            copied_data.original_panel_id = obj_panel_id; // Store original panel ID
                            this.clipboard_multi_object_data.push(copied_data);
                        } catch (error) {
                            console.error("Error copying object:", error);
                        }
                    }
                }
                
                // Set status based on how many objects were copied
                if (this.clipboard_multi_object_data.length > 0) {
                    // Also set the primary object to clipboard_object_data for compatibility
                    this.clipboard_object_data = this.clipboard_multi_object_data[0];
                    this.status_message = `${this.clipboard_multi_object_data.length} objects copied`;
                } else {
                    this.status_message = this.get_t('status.failed_to_copy_selected_objects');
                }
                
                return; // Skip single-object logic
            }
            
            // SINGLE OBJECT COPY PATH (original behavior)
            const object_id = typeof this.selected_object.id === 'function' ? this.selected_object.id() : this.selected_object.id;
            if (!object_id) {
                this.status_message = this.get_t('status.selected_object_id_missing');
                console.error("Copy failed: Selected object is missing an ID.");
                return;
            }
            
            const object_data = panel_data.content.find(obj => obj && obj.id === object_id); 
        
            if (!object_data) {
                this.status_message = this.get_t('status.object_data_not_found_in_panel_content');
                console.error(`Copy failed: Object data for ID '${object_id}' not found in panel content for panel ID '${panel_id}', panel is global: '${is_global_attr}'. Content IDs:`, panel_data.content.map(o => o ? o.id : 'null/undefined'));
                return;
            }
        
            // Perform deep copy using JSON methods
            try {
                this.clipboard_object_data = JSON.parse(JSON.stringify(object_data));
                this.clipboard_multi_object_data = [this.clipboard_object_data]; // Also store in array for consistency
                this.status_message = this.get_t('status.object_copied');
            } catch (error) {
                this.status_message = this.get_t('status.failed_to_serialize_object_data');
                console.error("Copy failed: Error during JSON stringify/parse:", error, "Object data:", object_data);
                this.clipboard_object_data = null;
            }
        },     
        
        /**
         * Pastes the copied object(s) into the currently selected panel.
         * If no panel is selected, uses the last active panel.
         * If multiple objects were copied, all will be pasted.
         */
        async paste_copied_object() {
            // Check if there's a valid image in system clipboard
            try {
                if (navigator.clipboard && navigator.clipboard.read) {
                    const clipboard_items = await navigator.clipboard.read();
                    for (const clipboard_item of clipboard_items) {
                        if (clipboard_item.types.some(type => type.startsWith('image/'))) {
                            // Clear internal clipboard since user has copied external image
                            this.clipboard_object_data = null;
                            this.clipboard_multi_object_data = [];
                            // Paste from system clipboard
                            await this.paste_image_from_clipboard();
                            return;
                        }
                    }
                }
            } catch (error) {
                // Continue with internal clipboard if system clipboard access fails
            }
            
            // Check if we have any internal objects to paste
            if (!this.clipboard_object_data && (!this.clipboard_multi_object_data || this.clipboard_multi_object_data.length === 0)) {
                // No internal objects and no system image, try fallback
                await this.paste_image_from_clipboard();
                return;
            }
            
            let target_panel = this.selected_panel;
            if (!target_panel) {
                this.status_message = this.get_t('status.no_panel_selected'); //Should not be possible
                console.error("Paste failed: Target panel failed.");
                return;
            }

            // Check premium limit for layers
            const current_layer_count = this.get_panel_layer_count(target_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }

            // Calculate center of the target panel's content area (relative to the panel group)
            const bounds = this.calculate_polygon_bounds(target_panel.points);
            const center_x = bounds.min_x + bounds.width / 2;
            const center_y = bounds.min_y + bounds.height / 2;
            
            // Check if we're pasting multiple objects
            const has_multiple_objects = this.clipboard_multi_object_data && this.clipboard_multi_object_data.length > 1;
            const created_objects = [];
            
            if (has_multiple_objects) {
                // Generate a timestamp to use for all pasted objects (to keep them close in ID)
                const paste_timestamp = Date.now();
                
                // Multi-object paste path
                for (let i = 0; i < this.clipboard_multi_object_data.length; i++) {
                    // Deep copy each object's data
                    const obj_data = JSON.parse(JSON.stringify(this.clipboard_multi_object_data[i]));
                    
                    // Set position (could add slight offset for each object to avoid perfect overlap)
                    const offset_x = (i - (this.clipboard_multi_object_data.length - 1) / 2) * 10;
                    const offset_y = (i - (this.clipboard_multi_object_data.length - 1) / 2) * 10;
                    obj_data.x = center_x + offset_x;
                    obj_data.y = center_y + offset_y;
                    
                    // Generate a new unique ID for the pasted object
                    const base_id = obj_data.id.split('_copy_')[0]; // Get original ID base
                    obj_data.id = `${base_id}_copy_${paste_timestamp}_${i}`;
                    
                    // Assign correct layer name
                    obj_data.layer_name = this.get_next_layer_name(target_panel);
                      // Recreate the object using existing functions
                    let created_object = null;
                    if (obj_data.object_type === 'image') {
                        created_object = await this.recreate_image_from_data(obj_data, target_panel.content_group);
                    } else if (obj_data.object_type === 'balloon') {
                        created_object = await this.recreate_balloon_from_data(obj_data, target_panel.content_group, true);
                    } else if (obj_data.object_type === 'standalone_text') {
                        created_object = await this.recreate_standalone_text_from_data(obj_data, target_panel.content_group);
                    } else {
                        console.warn("Skipping unsupported object type:", obj_data.object_type);
                        continue;
                    }
                    
                    if (created_object) {
                        created_objects.push(created_object);
                    }
                }
                
                // Select all created objects
                if (created_objects.length > 0) {
                    this.$nextTick(() => {
                        // Clear current selection
                        this.deselect_object();
                        
                        // Select first object to establish primary selection
                        this.select_object(created_objects[0]);
                        
                        // Add remaining objects to selection
                        for (let i = 1; i < created_objects.length; i++) {
                            this.add_object_to_array(created_objects[i]);
                        }
                        
                        // Update transformer to show multiple selection
                        if (this.selected_object_array.length > 1) {
                            this.transformer.nodes(this.selected_object_array);
                            this.redraw();
                        }
                    });
                }
                
                this.status_message = `${created_objects.length} objects pasted`;
            } 
            
            else {
                // Single object paste path (original behavior)
                // Deep copy object data
                const obj_data = JSON.parse(JSON.stringify(this.clipboard_object_data));
                
                // Set object position to the center of the target panel
                obj_data.x = center_x;
                obj_data.y = center_y;

                // Generate a new unique ID for the pasted object
                const base_id = obj_data.id.split('_copy_')[0]; // Get original ID base
                obj_data.id = `${base_id}_copy_${Date.now()}`;

                // Assign correct layer name
                obj_data.layer_name = this.get_next_layer_name(target_panel);
                let created_object = null;
                // Recreate the object using existing functions
                if (obj_data.object_type === 'image') {
                    created_object = await this.recreate_image_from_data(obj_data, target_panel.content_group);
                } else if (obj_data.object_type === 'balloon') {
                    created_object = await this.recreate_balloon_from_data(obj_data, target_panel.content_group, true);
                } else if (obj_data.object_type === 'standalone_text') {
                    created_object = await this.recreate_standalone_text_from_data(obj_data, target_panel.content_group);
                } else {
                    this.status_message = "Unsupported object type for pasting";
                    return;
                }

                if (created_object) {
                    this.$nextTick(() => {
                        this.select_object(created_object);
                    });
                }
                  this.status_message = "Object pasted";
            }

            this.redraw();
            // Handle deletion if it was a cut operation BEFORE recording history
            let is_cut_operation = false;
            if ((this.clipboard_object_data && this.clipboard_object_data.is_cut_operation === true) || 
                (this.clipboard_multi_object_data && this.clipboard_multi_object_data.some(obj => obj.is_cut_operation === true))) {
                
                is_cut_operation = true;
                const has_multiple_cut_objects = this.clipboard_multi_object_data && 
                                                 this.clipboard_multi_object_data.length > 1 &&
                                                 this.clipboard_multi_object_data.some(obj => obj.is_cut_operation === true);
                
                // Save currently selected objects after paste
                const currently_selected_after_paste = this.selected_object;
                const currently_selected_array_after_paste = [...this.selected_object_array];
                
                // Set flag to prevent history recording during cut cleanup
                this.is_performing_cut_cleanup = true;
                
                if (has_multiple_cut_objects) {
                    // For multiple objects
                    for (const clipboard_obj of this.clipboard_multi_object_data) {
                        if (clipboard_obj.is_cut_operation !== true) continue;
                        
                        const original_object_id = clipboard_obj.original_object_id_to_delete;
                        const original_panel_id = clipboard_obj.original_panel_id;
                        
                        if (original_object_id && original_panel_id) {
                            const original_panel = this.panels.find(p => (typeof p.id === 'function' ? p.id() : p.id) === original_panel_id);
                            if (original_panel && original_panel.content_group) {
                                const object_to_delete = original_panel.content_group.findOne('#' + original_object_id);
                                if (object_to_delete) {
                                    this.select_object(object_to_delete);
                                    this.delete_selected_object();
                                }
                            }
                        }
                        
                        // Clean up cut operation flags
                        delete clipboard_obj.is_cut_operation;
                        delete clipboard_obj.original_object_id_to_delete;
                        delete clipboard_obj.original_panel_id;
                    }
                    
                    // Re-select the pasted objects
                    if (currently_selected_array_after_paste.length > 0 && currently_selected_after_paste) {
                        // Clear current selection first
                        this.deselect_object();
                        
                        // Select primary object
                        if (currently_selected_after_paste.getStage()) {
                            this.select_object(currently_selected_after_paste);
                            
                            // Add rest of objects to selection
                            for (let i = 1; i < currently_selected_array_after_paste.length; i++) {
                                const obj = currently_selected_array_after_paste[i];
                                if (obj && obj.getStage()) {
                                    this.add_object_to_array(obj);
                                }
                            }
                            
                            // Update transformer
                            if (this.selected_object_array.length > 1) {
                                this.transformer.nodes(this.selected_object_array);
                                this.redraw();
                            }
                        }
                    }
                } else {
                    // Single object cut path (original behavior)
                    const original_object_id = this.clipboard_object_data.original_object_id_to_delete;
                    const original_panel_id = this.clipboard_object_data.original_panel_id;
    
                    if (original_object_id && original_panel_id) {
                        const original_panel = this.panels.find(p => (typeof p.id === 'function' ? p.id() : p.id) === original_panel_id);
                        if (original_panel && original_panel.content_group) {
                            const object_to_delete = original_panel.content_group.findOne('#' + original_object_id);
                            if (object_to_delete) {
                                this.select_object(object_to_delete); // Select original to delete it
                                this.delete_selected_object(); // This will deselect and update history
    
                                // Re-select the pasted object if it still exists and is not the one deleted
                                if (currently_selected_after_paste && currently_selected_after_paste.getStage()) {
                                    this.select_object(currently_selected_after_paste);
                                }
                            }
                        }
                    }
                    // Clean up cut operation flags from clipboard
                    delete this.clipboard_object_data.is_cut_operation;
                    delete this.clipboard_object_data.original_object_id_to_delete;
                    delete this.clipboard_object_data.original_panel_id;
                }
                  // Clear the flag after cut cleanup is complete
                this.is_performing_cut_cleanup = false;
            }
            
            // Record history with appropriate action name after cut cleanup is complete
            const action_name = is_cut_operation ? 'cut_and_paste_object' : 'paste_object';
            this.push_to_history(action_name);
            this.redraw();
        },
        
        /**
         * Marks the selected object(s) for cutting. Copies to clipboard with a flag.
         * When multiple objects are selected, all will be cut.
         */
        async cut_selected_object() {
            if (!this.selected_object) {
                this.status_message = "No object selected";
                return;
            }
            
            // Clear external clipboard to ensure internal clipboard takes priority
            this.clear_external_clipboard();
            
            // Store original IDs before copying
            const has_multiple_objects = this.selected_object_array && this.selected_object_array.length > 1;
            
            if (has_multiple_objects) {
                // For multiple objects, we'll handle them differently
                // First get IDs for all selected objects before copy operation
                const selected_objects_info = this.selected_object_array.map(obj => {
                    return {
                        object_id: typeof obj.id === 'function' ? obj.id() : obj.id,
                        panel: this.find_parent_panel_for_object(obj),
                    };
                });
                
                // Now perform the copy
                this.copy_selected_object();
                
                // Mark all items as cut operations
                if (this.clipboard_multi_object_data.length > 0) {
                    for (let i = 0; i < this.clipboard_multi_object_data.length; i++) {
                        if (i < selected_objects_info.length) {
                            const obj_info = selected_objects_info[i];
                            const panel_id = obj_info.panel ? 
                                (typeof obj_info.panel.id === 'function' ? obj_info.panel.id() : obj_info.panel.id) : null;
                                
                            this.clipboard_multi_object_data[i].is_cut_operation = true;
                            this.clipboard_multi_object_data[i].original_object_id_to_delete = obj_info.object_id;
                            this.clipboard_multi_object_data[i].original_panel_id = panel_id;
                        }
                    }
                    
                    // Also mark the primary clipboard object
                    if (this.clipboard_object_data) {
                        this.clipboard_object_data.is_cut_operation = true;
                        this.clipboard_object_data.original_object_id_to_delete = selected_objects_info[0].object_id;
                        this.clipboard_object_data.original_panel_id = selected_objects_info[0].panel ? 
                            (typeof selected_objects_info[0].panel.id === 'function' ? 
                                selected_objects_info[0].panel.id() : selected_objects_info[0].panel.id) : null;
                    }
                    
                    this.status_message = `${this.clipboard_multi_object_data.length} objects cut to clipboard`;
                } else {
                    this.status_message = "Failed to cut objects";
                }
                
                return;
            }
            
            // Single object cut path (original behavior)
            const original_selected_object_id = typeof this.selected_object.id === 'function' ? this.selected_object.id() : this.selected_object.id;
            const parent_panel_of_selected = this.find_parent_panel_for_object(this.selected_object);
            const original_parent_panel_id = parent_panel_of_selected ? (typeof parent_panel_of_selected.id === 'function' ? parent_panel_of_selected.id() : parent_panel_of_selected.id) : null;

            this.copy_selected_object(); // Standard copy to clipboard

            if (this.clipboard_object_data) {
                this.clipboard_object_data.is_cut_operation = true;
                this.clipboard_object_data.original_object_id_to_delete = original_selected_object_id;
                this.clipboard_object_data.original_panel_id = original_parent_panel_id;
                this.status_message = "Object cut to clipboard";
            } else {
                this.status_message = "Failed to cut object"; // copy_selected_object might have failed
            }
        },

        // Panel specific methods
        update_selected_panel_background_color() {
            if (!this.selected_panel) return;
            
            const background = this.selected_panel.background;
            if (background) {
                background.fill(this.selected_panel_background_color);
                this.redraw();
            }
        },
        
        toggle_panel_transform() {
            if (!this.selected_panel) return;

            this.deselect_subtool(); 
            this.panel_edit_proportional_mode = true; // Reset proportional mode
            
            this.panel_edit_mode_active = !this.panel_edit_mode_active;
            
            if (this.panel_edit_mode_active) {
                this.enter_panel_edit_mode(this.selected_panel);
            } else {
                this.exit_panel_edit_mode();
            }
        },

        toggle_panel_respect_margins(respect_margins) {
            if (!this.selected_panel) return;
            
            this.selected_panel.setAttr('respect_margins', respect_margins);
            this.push_to_history('toggle_panel_respect_margins');
        },

        show_respect_margins_help() {
            const html_content = `
                <div class="space-y-4">
                    <h3 class="text-lg font-medium text-white mb-2">${this.get_t('panel.respect_margins')}</h3>
                    <div class="space-y-3">
                        <div class="flex items-start gap-3">
                            <i data-lucide="check-circle" class="w-5 h-5 mt-0.5 text-green-400"></i>
                            <div>
                                <strong class="text-green-400">${this.get_t('panel.margins_respected')}:</strong>
                                <p class="text-gray-300 mt-1">${this.get_t('panel.margins_respected_desc')}</p>
                            </div>
                        </div>
                        <div class="flex items-start gap-3">
                            <i data-lucide="x-circle" class="w-5 h-5 mt-0.5 text-blue-400"></i>
                            <div>
                                <strong class="text-blue-400">${this.get_t('panel.margins_ignored')}:</strong>
                                <p class="text-gray-300 mt-1">${this.get_t('panel.margins_ignored_desc')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            this.show_popup_with_content(this.get_t('help.margin_constraints'), html_content, { type: 'info' });
        },

        enter_panel_edit_mode(panel) {
            if (!panel || panel.getAttr('is_global')) return;
            
            // Exit any existing edit mode first
            if (this.editing_panel) {
                this.exit_panel_edit_mode();
            }
            
            this.editing_panel = panel;
            this.panel_edit_mode_active = true;
            this.hide_transformer();
            
            // Disable panel selection while editing
            this.panel_selection_disabled = true;
            
            // Create edit handles for panel nodes
            this.create_panel_edit_handles(panel);
            
            // Update UI
            this.stage.container().style.cursor = 'default';
        },

        exit_panel_edit_mode() {
            if (!this.editing_panel) return;
            
            this.remove_panel_edit_handles(this.editing_panel);
            this.editing_panel = null;
            this.panel_edit_mode_active = false;
            this.panel_selection_disabled = false;
            
            // Clean up edge drag blocking state
            this.edge_drag_blocked_handles.clear();
            this.current_drag_handle = null;
            
            // Reset cursor
            this.stage.container().style.cursor = 'default';
        },
        
        create_panel_edit_handles(panel) {
            if (!panel.points || panel.points.length < 6) return;
            
            // Remove existing handles first
            this.remove_panel_edit_handles(panel);
            
            // Create vertex handles (red circles for corners)
            for (let i = 0; i < panel.points.length; i += 2) {
                const x = panel.points[i];
                const y = panel.points[i + 1];
                const node_index = i / 2;
                  // Create vertex handle
                const vertex_handle = new Konva.Circle({
                    x: panel.x() + x,
                    y: panel.y() + y,
                    radius: 8,
                   
                    fill: '#ff4444',
                    stroke: '#ffffff',
                    strokeWidth: 2,
                    name: 'panel_vertex_handle',
                    draggable: true,
                    node_index: node_index,
                    panel_id: panel.id(),
                    cursor: 'pointer',
                    listening: true,
                    hitStrokeWidth: 4 // Larger hit area for easier clicking
                });
                  // Handle hover effects for vertices
                vertex_handle.on('mouseenter', () => {
                    vertex_handle.radius(10);
                    vertex_handle.fill('#ff2222');
                    this.ui_layer.batchDraw();
                    document.body.style.cursor = 'pointer';
                });
                
                vertex_handle.on('mouseleave', () => {
                    vertex_handle.radius(8);
                    vertex_handle.fill('#ff4444');
                    this.ui_layer.batchDraw();
                    document.body.style.cursor = 'default';
                });
                
                // Handle drag events for vertices
                vertex_handle.on('dragstart', () => {
                    document.body.style.cursor = 'grabbing';
                });
                vertex_handle.on('dragmove', (e) => {
                    const handle = e.target;
                    const index = handle.getAttr('node_index');
                    
                    // Calculate proposed new position
                    const local_x = handle.x() - panel.x();
                    const local_y = handle.y() - panel.y();
                    
                    // Create test points array with proposed position
                    const test_points = [...panel.points];
                    test_points[index * 2] = local_x;
                    test_points[index * 2 + 1] = local_y;
                    // Check if movement violates boundary limits
                    // Note: Allow orientation changes for vertex handles (crossing is permitted)
                    const bounds_valid = this.check_panel_within_bounds(panel, test_points);
                    
                    if (bounds_valid) {
                        // Movement is completely valid, apply it
                        panel.points[index * 2] = local_x;
                        panel.points[index * 2 + 1] = local_y;
                        
                        // Update panel shape and edge handles
                        this.update_panel_shape(panel);
                        this.update_edge_handles_position(panel);
                    } else {
                        // Try to apply partial movement (constrain only problematic axes)
                        let final_x = panel.points[index * 2]; // Current X
                        let final_y = panel.points[index * 2 + 1]; // Current Y
                          // Test X movement only
                        const test_x_points = [...panel.points];
                        test_x_points[index * 2] = local_x;
                        if (this.check_panel_within_bounds(panel, test_x_points)) {
                            final_x = local_x;
                        }
                        
                        // Test Y movement only
                        const test_y_points = [...panel.points];
                        test_y_points[index * 2 + 1] = local_y;
                        if (this.check_panel_within_bounds(panel, test_y_points)) {
                            final_y = local_y;
                        }
                        
                        // Apply partial movement
                        panel.points[index * 2] = final_x;
                        panel.points[index * 2 + 1] = final_y;
                        
                        // Update handle position to reflect actual position
                        handle.x(panel.x() + final_x);
                        handle.y(panel.y() + final_y);
                        
                        // Update panel shape and edge handles
                        this.update_panel_shape(panel);
                        this.update_edge_handles_position(panel);
                    }
                });
                
                vertex_handle.on('dragend', () => {
                    document.body.style.cursor = 'pointer';
                    this.push_to_history('edit_panel_shape');
                });
                
                this.ui_layer.add(vertex_handle);
                vertex_handle.moveToTop(); // Ensure handles are always on top
            }
            // Create edge handles (green circles for side centers)
            // Create edge handles for all panels regardless of size
            for (let i = 0; i < panel.points.length; i += 2) {
                const current_index = i / 2;
                const next_index = (current_index + 1) % (panel.points.length / 2);
                
                const x1 = panel.points[current_index * 2];
                const y1 = panel.points[current_index * 2 + 1];
                const x2 = panel.points[next_index * 2];
                const y2 = panel.points[next_index * 2 + 1];
                
                // Calculate midpoint
                const mid_x = (x1 + x2) / 2;
                const mid_y = (y1 + y2) / 2;
                
                // Create edge handle (circular, not square)
                const edge_handle = new Konva.Circle({
                    x: panel.x() + mid_x,
                        y: panel.y() + mid_y,
                        radius: 6,
                        fill: '#44ff44',
                        stroke: '#ffffff',
                        strokeWidth: 2,
                        name: 'panel_edge_handle',
                        draggable: true,
                        edge_index: current_index,
                        panel_id: panel.id(),
                        cursor: 'pointer',
                        listening: true,
                        hitStrokeWidth: 4 // Larger hit area for easier clicking
                    });
                    
                    // Handle hover effects for edges
                    edge_handle.on('mouseenter', () => {
                        edge_handle.radius(8);
                        edge_handle.fill('#22ff22');
                        this.ui_layer.batchDraw();
                        document.body.style.cursor = 'pointer';
                    });
                    
                    edge_handle.on('mouseleave', () => {
                        edge_handle.radius(6);
                        edge_handle.fill('#44ff44');
                        this.ui_layer.batchDraw();
                        document.body.style.cursor = 'default';
                    });
                      // Handle drag events for edges
                    edge_handle.on('dragstart', (e) => {
                        document.body.style.cursor = 'grabbing';
                        
                        // Initialize blocking state for this handle
                        const handle = e.target;
                        const handle_id = handle._id;
                        this.current_drag_handle = handle;
                        
                        // Reset blocking state for this handle
                        this.edge_drag_blocked_handles.set(handle_id, {
                            blocked: false,
                            initial_direction: null
                        });
                    });
                    
                    edge_handle.on('dragmove', (e) => {
                        const handle = e.target;
                        const edge_index = handle.getAttr('edge_index');
                        
                        // Move the entire edge with correct behavior
                        this.move_panel_edge_correctly(panel, edge_index, handle);
                    });                    
                    edge_handle.on('dragend', () => {
                        document.body.style.cursor = 'pointer';
                        
                        // Clean up blocking state for this handle
                        if (this.current_drag_handle) {
                            const handle_id = this.current_drag_handle._id;
                            this.edge_drag_blocked_handles.delete(handle_id);
                            this.current_drag_handle = null;
                        }
                        
                        this.push_to_history('edit_panel_shape');
                    });
                    
                    this.ui_layer.add(edge_handle);
                    edge_handle.moveToTop(); // Ensure handles are always on top
                }
            
            // Create center handle for moving the entire panel
            const bounds = this.get_panel_bounds(panel);
            const center_x = bounds.minX + bounds.width / 2;
            const center_y = bounds.minY + bounds.height / 2;
            
            const center_handle = new Konva.Path({
                x: panel.x() + center_x,
                y: panel.y() + center_y,
                data: 'M -8 -2 L -2 -2 L -2 -8 L 2 -8 L 2 -2 L 8 -2 L 8 2 L 2 2 L 2 8 L -2 8 L -2 2 L -8 2 Z', // Plus/move icon
                fill: '#0096ff',
                stroke: '#ffffff',
                strokeWidth: 1,
                name: 'panel_center_handle',
                draggable: true,
                panel_id: panel.id(),
                cursor: 'move',
                listening: true,
                hitStrokeWidth: 4
            });
            
            // Handle hover effects for center handle
            center_handle.on('mouseenter', () => {
                center_handle.scaleX(1.2);
                center_handle.scaleY(1.2);
                center_handle.fill('#0088dd');
                this.ui_layer.batchDraw();
                document.body.style.cursor = 'move';
            });
            
            center_handle.on('mouseleave', () => {
                center_handle.scaleX(1);
                center_handle.scaleY(1);
                center_handle.fill('#0096ff');
                this.ui_layer.batchDraw();
                document.body.style.cursor = 'default';
            });
            
            // Handle drag events for center handle
            center_handle.on('dragstart', () => {
                document.body.style.cursor = 'grabbing';
            });
            
            center_handle.on('dragmove', (e) => {
                const handle = e.target;
                
                // Calculate movement delta
                const new_center_x = handle.x() - panel.x();
                const new_center_y = handle.y() - panel.y();
                const bounds = this.get_panel_bounds(panel);
                const old_center_x = bounds.minX + bounds.width / 2;
                const old_center_y = bounds.minY + bounds.height / 2;
                
                const delta_x = new_center_x - old_center_x;
                const delta_y = new_center_y - old_center_y;
                
                // Calculate new panel position
                let new_panel_x = panel.x() + delta_x;
                let new_panel_y = panel.y() + delta_y;
                
                // Check bounds only if panel respects margins
                if (panel.getAttr('respect_margins')) {
                    const panel_bounds = this.get_panel_bounds(panel);
                    const panel_left = new_panel_x + panel_bounds.minX;
                    const panel_right = new_panel_x + panel_bounds.maxX;
                    const panel_top = new_panel_y + panel_bounds.minY;
                    const panel_bottom = new_panel_y + panel_bounds.maxY;
                    
                    const page_left = this.page_margin;
                    const page_right = this.canvas_width - this.page_margin;
                    const page_top = this.page_margin;
                    const page_bottom = this.canvas_height - this.page_margin;
                    
                    // Allow movement in directions that don't violate bounds
                    if (panel_left < page_left || panel_right > page_right) {
                        new_panel_x = panel.x(); // Don't move horizontally
                    }
                    if (panel_top < page_top || panel_bottom > page_bottom) {
                        new_panel_y = panel.y(); // Don't move vertically
                    }
                }
                
                // Move panel to new position
                panel.x(new_panel_x);
                panel.y(new_panel_y);
                
                // Update all handle positions
                this.update_all_handles_position(panel);
            });
            
            center_handle.on('dragend', () => {
                document.body.style.cursor = 'move';
                this.push_to_history('move_panel');
            });
            
            this.ui_layer.add(center_handle);
            center_handle.moveToTop();
            
            this.ui_layer.batchDraw();
        },

        remove_panel_edit_handles(panel) {
            // Remove vertex handles
            const vertex_handles = this.ui_layer.find('.panel_vertex_handle');
            vertex_handles.forEach(handle => {
                if (panel && handle.getAttr('panel_id') && handle.getAttr('panel_id') !== panel.id()) {
                    return;
                }
                handle.destroy();
            });
            
            // Remove edge handles
            const edge_handles = this.ui_layer.find('.panel_edge_handle');
            edge_handles.forEach(handle => {
                if (panel && handle.getAttr('panel_id') && handle.getAttr('panel_id') !== panel.id()) {
                    return;
                }
                
                // Clean up blocking state for this handle if it exists
                const handle_id = handle._id;
                if (this.edge_drag_blocked_handles.has(handle_id)) {
                    this.edge_drag_blocked_handles.delete(handle_id);
                }
                
                handle.destroy();
            });
            
            // Remove center handles
            const center_handles = this.ui_layer.find('.panel_center_handle');
            center_handles.forEach(handle => {
                if (panel && handle.getAttr('panel_id') && handle.getAttr('panel_id') !== panel.id()) {
                    return;
                }
                handle.destroy();
            });
            
            // Reset current drag handle if it was removed
            this.current_drag_handle = null;
            
            this.ui_layer.batchDraw();
            document.body.style.cursor = 'default';
        },
        
        get_panel_bounds(panel) {
            if (!panel.points || panel.points.length === 0) {
                return { width: 0, height: 0, minX: 0, maxX: 0, minY: 0, maxY: 0 };
            }
            
            let minX = panel.points[0];
            let maxX = panel.points[0];
            let minY = panel.points[1];
            let maxY = panel.points[1];
            
            for (let i = 2; i < panel.points.length; i += 2) {
                const x = panel.points[i];
                const y = panel.points[i + 1];
                
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
            
            return {
                width: maxX - minX,
                height: maxY - minY,
                minX: minX,
                maxX: maxX,
                minY: minY,
                maxY: maxY
            };
        },

        update_panel_shape(panel) {
            if (!panel.points) return;
            
            // Update background shape
            if (panel.background) {
                panel.background.points(panel.points);
            }
            
            // Update outline
            if (panel.outline) {
                panel.outline.points(panel.points);
            }
            
            // Update content clipping
            if (panel.content_group) {
                panel.content_group.clipFunc((ctx) => {
                    ctx.beginPath();
                    for (let i = 0; i < panel.points.length; i += 2) {
                        const x = panel.points[i];
                        const y = panel.points[i + 1];
                        if (i === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    }
                    ctx.closePath();
                });
            }
            
            // Update any borders or decorations
            if (panel.border) {
                panel.border.points(panel.points);
            }
            
            // Force redraw
            this.panels_layer.batchDraw();
            // Update edit handles position if in edit mode
            if (this.panel_edit_mode_active && this.editing_panel === panel) {
                this.update_all_handles_position(panel);
            }
        },

        get_selected_panel_background_color() {
            if (!this.selected_panel || !this.selected_panel.background) return '#ffffff';
            return this.selected_panel.background.fill() || '#ffffff';
        },
        
        check_panel_orientation(panel_points) {
            if (!panel_points || panel_points.length !== 8) return true;
            
            // Calculate the signed area using shoelace formula
            // Positive area = counter-clockwise (correct)
            // Negative area = clockwise (inverted)
            let signed_area = 0;
            for (let i = 0; i < 4; i++) {
                const x1 = panel_points[i * 2];
                const y1 = panel_points[i * 2 + 1];
                const x2 = panel_points[((i + 1) % 4) * 2];
                const y2 = panel_points[((i + 1) % 4) * 2 + 1];
                signed_area += x1 * y2 - x2 * y1;
            }
            
            // Return true if counter-clockwise (positive area), false if inverted
            return signed_area > 0;
        },

        calculate_panel_area(panel_points) {
            if (!panel_points || panel_points.length !== 8) return 0;
            
            // Calculate area using shoelace formula
            let area = 0;
            for (let i = 0; i < 4; i++) {
                const x1 = panel_points[i * 2];
                const y1 = panel_points[i * 2 + 1];
                const x2 = panel_points[((i + 1) % 4) * 2];
                const y2 = panel_points[((i + 1) % 4) * 2 + 1];
                area += x1 * y2 - x2 * y1;
            }
            return Math.abs(area) / 2;
        },

        /**
         * Clears the external system clipboard by writing empty text
         * This ensures internal clipboard takes priority over external images
         */
        clear_external_clipboard() {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText('').catch(() => {
                        // Ignore errors when clearing clipboard
                    });
                }
            } catch (error) {
                // Ignore clipboard access errors
            }
        },

        /**
         * Sets up drag and drop functionality for external images
         */
        setup_drag_and_drop() {
            const canvas_wrapper = document.getElementById('canvas_wrapper');
            if (!canvas_wrapper) return;

            // Prevent default drag behaviors and add visual feedback
            canvas_wrapper.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                canvas_wrapper.classList.add('drag-over');
            });

            canvas_wrapper.addEventListener('dragenter', (e) => {
                e.preventDefault();
                canvas_wrapper.classList.add('drag-over');
            });

            canvas_wrapper.addEventListener('dragleave', (e) => {
                e.preventDefault();
                if (!canvas_wrapper.contains(e.relatedTarget)) {
                    canvas_wrapper.classList.remove('drag-over');
                }
            });

            canvas_wrapper.addEventListener('drop', (e) => {
                e.preventDefault();
                canvas_wrapper.classList.remove('drag-over');
                this.handle_image_drop(e);
            });
        },

        /**
         * Handles dropped image files
         */
        handle_image_drop(e) {
            // Check for AI preview image data first
            const ai_preview_url = e.dataTransfer.getData('text/ai-preview-url');
            
            if (ai_preview_url) {
                // Handle AI preview image drop
                this.handle_ai_preview_drop(e, ai_preview_url);
                return;
            }
            
            // Handle regular file drops
            const files = Array.from(e.dataTransfer.files);
            const image_files = files.filter(file => file.type.startsWith('image/'));
            
            if (image_files.length === 0) {
                return;
            }

            // Get drop position from event coordinates (reliable)
            const canvas_rect = document.getElementById('canvas_wrapper').getBoundingClientRect();
            const drop_x = e.clientX - canvas_rect.left;
            const drop_y = e.clientY - canvas_rect.top;
            
            // Convert to canvas coordinates manually (get_pointer_position() is inconsistent)
            const canvas_coords = {
                x: (drop_x - this.stage.x()) / this.canvas_zoom,
                y: (drop_y - this.stage.y()) / this.canvas_zoom
            };

            // Find target panel - prioritize selected panel if drop is over it
            let target_panel = null;
            
            // First check if drop is over the selected panel
            // if (this.selected_panel && !this.selected_panel.getAttr('is_global')) {
            //     const panel_x = this.selected_panel.x();
            //     const panel_y = this.selected_panel.y();
            //     const point = { x: canvas_coords.x - panel_x, y: canvas_coords.y - panel_y };
                
            //     if (this.is_point_in_polygon(point, this.selected_panel.points)) {
            //         target_panel = this.selected_panel;
            //     }
            // }
            
            // If not over selected panel, find topmost panel at drop position
            if (!target_panel) {
                for (let i = this.panels.length - 1; i >= 0; i--) {
                    const panel = this.panels[i];
                    if (panel.getAttr('is_global')) continue; // Skip global panel for now
                    
                    const panel_x = panel.x();
                    const panel_y = panel.y();
                    const point = { x: canvas_coords.x - panel_x, y: canvas_coords.y - panel_y };
                    
                    if (this.is_point_in_polygon(point, panel.points)) {
                        target_panel = panel;
                        break;
                    }
                }
            }
            
            // If no specific panel found, use global panel
            if (!target_panel) {
                target_panel = this.panels.find(panel => panel.getAttr('is_global'));
                if (!target_panel) {
                    return;
                }
            }

            // Check premium limit using existing function
            const current_layer_count = this.get_panel_layer_count(target_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }

            // Process first image file
            const file = image_files[0];
            const reader = new FileReader();
            
            reader.onload = (event) => {
                // Create image using existing function but with custom positioning
                this.create_image_in_panel_at_position(event.target.result, target_panel, canvas_coords);
            };
            
            reader.readAsDataURL(file);
        },

        /**
         * Handle AI preview image drop from drag operation
         */
        async handle_ai_preview_drop(e, ai_preview_url) {
            // Get drop position from event coordinates
            const canvas_rect = document.getElementById('canvas_wrapper').getBoundingClientRect();
            const drop_x = e.clientX - canvas_rect.left;
            const drop_y = e.clientY - canvas_rect.top;
            
            // Convert to canvas coordinates
            const canvas_coords = {
                x: (drop_x - this.stage.x()) / this.canvas_zoom,
                y: (drop_y - this.stage.y()) / this.canvas_zoom
            };

            // Find target panel using same logic as regular drops
            let target_panel = null;
            
            // If not over selected panel, find topmost panel at drop position
            if (!target_panel) {
                for (let i = this.panels.length - 1; i >= 0; i--) {
                    const panel = this.panels[i];
                    if (panel.getAttr('is_global')) continue; // Skip global panel for now
                    
                    const panel_x = panel.x();
                    const panel_y = panel.y();
                    const point = { x: canvas_coords.x - panel_x, y: canvas_coords.y - panel_y };
                    
                    if (this.is_point_in_polygon(point, panel.points)) {
                        target_panel = panel;
                        break;
                    }
                }
            }
            
            // If no specific panel found, use global panel
            if (!target_panel) {
                target_panel = this.panels.find(panel => panel.getAttr('is_global'));
                if (!target_panel) {
                    return;
                }
            }

            // Check premium limit using existing function
            const current_layer_count = this.get_panel_layer_count(target_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }

            // Deselect current object like accept_ai_image() does
            this.deselect_object();

            try {
                // Convert URL to base64 data exactly like accept_ai_image() does
                const base64_data = await this.url_to_base64(ai_preview_url);
                this.create_image_in_panel_at_position(base64_data, target_panel, canvas_coords);
                
                // Add to history like regular drag and drop
                this.push_to_history('add_ai_image_drag_drop');
                
            } catch (error) {
                console.error('Error converting AI image to base64 for drag and drop:', error);
                this.show_floating_message(this.get_t('ai.error_accepting_image'), 'error');
            }
        },

        /**
         * Handle cross-panel transfer when dragging is enabled
         */
        handle_cross_panel_transfer(dragged_object) {
            if (!dragged_object) return;
            
            // Get current panel
            const current_panel = dragged_object.getParent().getParent(); // object -> content_group -> panel
            if (!current_panel) return;
            
            // Get pointer position adjusted for zoom and pan
            const pointer_pos = this.get_pointer_position();
            if (!pointer_pos) return;
            
            const pointer_x = pointer_pos.x;
            const pointer_y = pointer_pos.y;
            
            // First, check if pointer is still within current panel
            if (!current_panel.getAttr('is_global')) {
                const panel_x = current_panel.x();
                const panel_y = current_panel.y();
                const point_in_current = { x: pointer_x - panel_x, y: pointer_y - panel_y };
                
                if (this.is_point_in_polygon(point_in_current, current_panel.points)) {
                    // Pointer is still within current panel, don't transfer
                    return;
                }
            }
            
            // Find target panel at pointer position (excluding current panel)
            let target_panel = null;
            for (let i = this.panels.length - 1; i >= 0; i--) {
                const panel = this.panels[i];
                if (panel === current_panel) continue; // Skip current panel
                
                if (panel.getAttr('is_global')) {
                    // Skip global panel for now, check regular panels first
                    continue;
                } else {
                    // For regular panels, check if pointer is within polygon
                    const panel_x = panel.x();
                    const panel_y = panel.y();
                    const point = { x: pointer_x - panel_x, y: pointer_y - panel_y };
                    
                    if (this.is_point_in_polygon(point, panel.points)) {
                        target_panel = panel;
                        break;
                    }
                }
            }
            
            // If no regular panel found, DON'T automatically transfer to global panel
            // Global panel transfers should only happen via the explicit button
            
            // If target panel found and different from current, transfer the object
            if (target_panel && target_panel !== current_panel) {
                // Get object's current global position for transfer
                const global_x = current_panel.x() + dragged_object.x();
                const global_y = current_panel.y() + dragged_object.y();
                this.transfer_object_to_panel(dragged_object, target_panel, { x: global_x, y: global_y });
            }
        },

        /**
         * Transfer an object from one panel to another
         */
        transfer_object_to_panel(object, target_panel, global_coords) {
            if (!object || !target_panel) return;
            
            // Check premium limit for target panel
            const current_layer_count = this.get_panel_layer_count(target_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }
            
            // Calculate new position relative to target panel
            const target_x = target_panel.x();
            const target_y = target_panel.y();
            const new_local_x = global_coords.x - target_x;
            const new_local_y = global_coords.y - target_y;
            
            // Remove object from current panel
            const current_panel = object.getParent().getParent();
            object.remove();
            
            // Add object to target panel with new coordinates
            object.x(new_local_x);
            object.y(new_local_y);
            target_panel.content_group.add(object);
            
            // Update layer name for consistency
            const new_layer_name = this.get_next_layer_name(target_panel);
            object.setAttr('layer_name', new_layer_name);
            
            // Select target panel and object
            this.select_panel(target_panel);
            
            // Force Vue reactivity: deselect and reselect to trigger computed property recalculation
            this.deselect_object(false);
            this.$nextTick(() => {
                this.select_object(object);
                
                // Update selected layer reference
                this.selected_layer = {
                    id: object.id(),
                    name: new_layer_name,
                    node: object,
                    visible: true,
                    locked: object.getAttr('locked') || false
                };
            });
            
            // Redraw everything
            this.panels_layer.draw();
            this.push_to_history('transfer_object_between_panels');
        },

        /**
         * Modified version of create_image_in_panel that accepts specific coordinates
         */
        create_image_in_panel_at_position(image_data, panel, target_coords) {
            const image = new Image();
            image.onload = () => {
                // Calculate panel bounds using existing function
                const bounds = this.calculate_polygon_bounds(panel.points);
                const content_width = Math.max(bounds.width, 50);
                const content_height = Math.max(bounds.height, 50);
                
                // Calculate scale to fit image reasonably
                const max_scale = 0.8; // Maximum 80% of panel size
                const scale_x = (content_width * max_scale) / image.width;
                const scale_y = (content_height * max_scale) / image.height;
                const scale = Math.min(scale_x, scale_y, 1);
                
                // Convert target position to panel-relative coordinates
                const panel_x = panel.x();
                const panel_y = panel.y();
                const local_x = target_coords.x - panel_x;
                const local_y = target_coords.y - panel_y;
                
                // Use existing layer naming function
                const layer_name = this.get_next_layer_name(panel);
                
                // Create Konva image using same structure as existing function
                const konva_image_raw = new Konva.Image({
                    image: image,
                    x: local_x,
                    y: local_y,
                    offset: {
                        x: image.width / 2,
                        y: image.height / 2
                    },
                    scaleX: scale,
                    scaleY: scale,
                    draggable: this.active_tool === 'select',
                    name: 'panel_image',
                    id: 'image_' + Date.now(),
                    listening: true,
                    globalCompositeOperation: 'normal',
                    opacity: 1
                });

                const konva_image = markRaw(konva_image_raw);

                // Set attributes like existing function
                konva_image.setAttr('src_data', image_data);
                konva_image.setAttr('layer_name', layer_name);
                konva_image.setAttr('locked', false);
                konva_image.setAttr('object_type', 'image');
                
                // Add to panel and select using existing pattern
                panel.content_group.add(konva_image);
                
                // Select the target panel to maintain consistency
                this.select_panel(panel);
                
                // Switch to select tool and select object
                this.change_tool('select');
                this.select_object(konva_image);

                // Create layer object like existing function
                this.selected_layer = {
                    id: konva_image.id(),
                    name: layer_name,
                    node: konva_image,
                    visible: true,
                    locked: false
                };
                
                // Add to clipboard for consistency with paste operations
                const object_data = {
                    id: konva_image.id(),
                    object_type: 'image',
                    layer_name: layer_name,
                    x: local_x,
                    y: local_y,
                    scaleX: scale,
                    scaleY: scale,
                    rotation: 0,
                    offsetX: image.width / 2,
                    offsetY: image.height / 2,
                    opacity: 1,
                    globalCompositeOperation: 'normal',
                    src_data: image_data,
                    locked: false
                };
                
                this.clipboard_object_data = object_data;
                this.clipboard_multi_object_data = [object_data];
                
                // Clear external clipboard
                this.clear_external_clipboard();
                
                // Draw and record history like existing functions
                this.panels_layer.draw();
                this.push_to_history('add_image_drag_drop');
            };
            
            image.src = image_data;
        },
        
        check_panel_minimum_size(panel_points, min_size = 50) {
            if (!panel_points || panel_points.length !== 8) return true;
            
            // Calculate bounding box first as a quick check
            let min_x = Math.min(panel_points[0], panel_points[2], panel_points[4], panel_points[6]);
            let max_x = Math.max(panel_points[0], panel_points[2], panel_points[4], panel_points[6]);
            let min_y = Math.min(panel_points[1], panel_points[3], panel_points[5], panel_points[7]);
            let max_y = Math.max(panel_points[1], panel_points[3], panel_points[5], panel_points[7]);
            
            const bbox_width = max_x - min_x;
            const bbox_height = max_y - min_y;
            
            // Quick bounding box check
            if (bbox_width < min_size || bbox_height < min_size) {
                return false;
            }
            
            // Calculate area using shoelace formula for more accurate size validation
            let area = 0;
            for (let i = 0; i < 4; i++) {
                const x1 = panel_points[i * 2];
                const y1 = panel_points[i * 2 + 1];
                const x2 = panel_points[((i + 1) % 4) * 2];
                const y2 = panel_points[((i + 1) % 4) * 2 + 1];
                area += x1 * y2 - x2 * y1;
            }
            area = Math.abs(area) / 2;
            
            // Minimum area check (more lenient than bbox)
            const min_area = min_size * min_size * 0.6; // Allow 60% of perfect square area
            if (area < min_area) {
                return false;
            }
            
            // Check minimum "thickness" in each direction
            // Calculate shortest distance between opposite edges
            for (let i = 0; i < 2; i++) { // Only check two perpendicular directions
                const edge1_start = i * 2;
                const edge1_end = ((i + 1) % 4) * 2;
                const edge2_start = ((i + 2) % 4) * 2;
                const edge2_end = ((i + 3) % 4) * 2;
                
                const x1 = panel_points[edge1_start];
                const y1 = panel_points[edge1_start + 1];
                const x2 = panel_points[edge1_end];
                const y2 = panel_points[edge1_end + 1];
                const x3 = panel_points[edge2_start];
                const y3 = panel_points[edge2_start + 1];
                const x4 = panel_points[edge2_end];
                const y4 = panel_points[edge2_end + 1];
                
                // Calculate minimum distance between the two parallel edges
                const edge1_length = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
                
                if (edge1_length > 0) {
                    // Distance from points on edge2 to line defined by edge1
                    const edge1_dx = x2 - x1;
                    const edge1_dy = y2 - y1;
                    
                    const dist1 = Math.abs(edge1_dy * x3 - edge1_dx * y3 + x2 * y1 - y2 * x1) / edge1_length;
                    const dist2 = Math.abs(edge1_dy * x4 - edge1_dx * y4 + x2 * y1 - y2 * x1) / edge1_length;
                    
                    const min_distance = Math.min(dist1, dist2);
                    
                    // If any direction is too thin, reject
                    if (min_distance < min_size * 0.8) {
                        return false;
                    }
                }
            }
            
            return true;
        },

        check_panel_within_bounds(panel, panel_points) {
            if (!panel_points || panel_points.length !== 8) return true;
            
            // If panel doesn't respect margins, allow any position
            if (!panel.getAttr('respect_margins')) return true;
            
            // Calculate panel bounds in global coordinates
            const panel_x = panel.x();
            const panel_y = panel.y();
            
            let min_x = Math.min(panel_points[0], panel_points[2], panel_points[4], panel_points[6]) + panel_x;
            let max_x = Math.max(panel_points[0], panel_points[2], panel_points[4], panel_points[6]) + panel_x;
            let min_y = Math.min(panel_points[1], panel_points[3], panel_points[5], panel_points[7]) + panel_y;
            let max_y = Math.max(panel_points[1], panel_points[3], panel_points[5], panel_points[7]) + panel_y;
            
            // Check against page boundaries (considering margin)
            const page_left = this.page_margin;
            const page_right = this.canvas_width - this.page_margin;
            const page_top = this.page_margin;
            const page_bottom = this.canvas_height - this.page_margin;
            
            return min_x >= page_left && max_x <= page_right && min_y >= page_top && max_y <= page_bottom;
        },


        move_panel_edge_correctly(panel, edge_index, handle) {
            if (!panel.points || panel.points.length !== 8) return; // Only works with rectangles
            
            const current_index = edge_index;
            const next_index = (current_index + 1) % 4;
            
            // Get edge vertices (the two vertices we will move)
            const v1_x = panel.points[current_index * 2];
            const v1_y = panel.points[current_index * 2 + 1];
            const v2_x = panel.points[next_index * 2];
            const v2_y = panel.points[next_index * 2 + 1];
            
            // Get true opposite vertices (non-crossed)
            // For a quadrilateral with vertices 0,1,2,3:
            // Edge 0-1: v1=0, v2=1, opposites are v1->3, v2->2
            // Edge 1-2: v1=1, v2=2, opposites are v1->0, v2->3
            // Edge 2-3: v1=2, v2=3, opposites are v1->1, v2->0
            // Edge 3-0: v1=3, v2=0, opposites are v1->2, v2->1
            const opp1_index = (current_index + 3) % 4; // True opposite of v1
            const opp2_index = (current_index + 2) % 4; // True opposite of v2
            const opp1_x = panel.points[opp1_index * 2];
            const opp1_y = panel.points[opp1_index * 2 + 1];
            const opp2_x = panel.points[opp2_index * 2];
            const opp2_y = panel.points[opp2_index * 2 + 1];
            
            // Calculate edge vector and perpendicular vector
            const edge_dx = v2_x - v1_x;
            const edge_dy = v2_y - v1_y;
            const edge_length = Math.sqrt(edge_dx * edge_dx + edge_dy * edge_dy);
            
            if (edge_length === 0) return;
            
            // Perpendicular unit vector (normal to edge)
            const perp_unit_x = -edge_dy / edge_length;
            const perp_unit_y = edge_dx / edge_length;
            
            // Calculate original edge midpoint
            const orig_mid_x = (v1_x + v2_x) / 2;
            const orig_mid_y = (v1_y + v2_y) / 2;
            
            // Get current handle position in local coordinates
            const handle_local_x = handle.x() - panel.x();
            const handle_local_y = handle.y() - panel.y();
            
            // Calculate movement vector from original midpoint
            const movement_x = handle_local_x - orig_mid_x;
            const movement_y = handle_local_y - orig_mid_y;
            
            // Project movement onto perpendicular direction only
            const perp_movement = movement_x * perp_unit_x + movement_y * perp_unit_y;
              let new_v1_x, new_v1_y, new_v2_x, new_v2_y;
            
            if (this.panel_edit_proportional_mode) {
                // Proportional mode: move vertices proportionally based on distance to opposites
                // Calculate direction vectors from each vertex to its true opposite vertex
                const dir1_x = opp1_x - v1_x;
                const dir1_y = opp1_y - v1_y;
                const dir2_x = opp2_x - v2_x;
                const dir2_y = opp2_y - v2_y;
                
                // Calculate distances to opposite vertices
                const dist1 = Math.sqrt(dir1_x * dir1_x + dir1_y * dir1_y);
                const dist2 = Math.sqrt(dir2_x * dir2_x + dir2_y * dir2_y);
                const total_distance = dist1 + dist2;
                
                if (total_distance === 0) return;
                
                // Calculate proportional movement ratios
                const ratio1 = dist1 / total_distance;
                const ratio2 = dist2 / total_distance;
                
                // Normalize direction vectors
                const norm_dir1_x = dist1 > 0 ? dir1_x / dist1 : 0;
                const norm_dir1_y = dist1 > 0 ? dir1_y / dist1 : 0;
                const norm_dir2_x = dist2 > 0 ? dir2_x / dist2 : 0;
                const norm_dir2_y = dist2 > 0 ? dir2_y / dist2 : 0;
                
                // Calculate how much each vertex should move towards its opposite
                const move_distance1 = perp_movement * ratio1;
                const move_distance2 = perp_movement * ratio2;
                
                // Calculate new positions by moving vertices towards their true opposites
                new_v1_x = v1_x + norm_dir1_x * move_distance1;
                new_v1_y = v1_y + norm_dir1_y * move_distance1;
                new_v2_x = v2_x + norm_dir2_x * move_distance2;
                new_v2_y = v2_y + norm_dir2_y * move_distance2;
            }
            
            else {
                // Smart linear mode: detect right angles and apply different movement
                // Check if vertices form right angles with their adjacent vertices
                const tolerance = 5; // Tolerance for right angle detection
                
                // For v1, check angle with previous vertex
                const prev1_index = (current_index + 3) % 4;
                const prev1_x = panel.points[prev1_index * 2];
                const prev1_y = panel.points[prev1_index * 2 + 1];
                
                // For v2, check angle with next vertex
                const next2_index = (next_index + 1) % 4;
                const next2_x = panel.points[next2_index * 2];
                const next2_y = panel.points[next2_index * 2 + 1];
                
                // Calculate vectors for angle detection
                const vec1_to_prev_x = prev1_x - v1_x;
                const vec1_to_prev_y = prev1_y - v1_y;
                const vec1_to_next_x = v2_x - v1_x;
                const vec1_to_next_y = v2_y - v1_y;
                
                const vec2_to_prev_x = v1_x - v2_x;
                const vec2_to_prev_y = v1_y - v2_y;
                const vec2_to_next_x = next2_x - v2_x;
                const vec2_to_next_y = next2_y - v2_y;
                
                // Check if v1 forms a right angle (perpendicular lines)
                const dot1 = vec1_to_prev_x * vec1_to_next_x + vec1_to_prev_y * vec1_to_next_y;
                const is_right_angle_v1 = Math.abs(dot1) < tolerance;
                
                // Check if v2 forms a right angle
                const dot2 = vec2_to_prev_x * vec2_to_next_x + vec2_to_prev_y * vec2_to_next_y;
                const is_right_angle_v2 = Math.abs(dot2) < tolerance;
                
                // Apply movement based on right angle detection (original logic)
                if (is_right_angle_v1) {
                    // v1 is at right angle: move linearly (perpendicular to edge)
                    new_v1_x = v1_x + perp_movement * perp_unit_x;
                    new_v1_y = v1_y + perp_movement * perp_unit_y;
                } else {
                    // v1 not at right angle: move proportionally
                    const dir1_x = opp1_x - v1_x;
                    const dir1_y = opp1_y - v1_y;
                    const dist1 = Math.sqrt(dir1_x * dir1_x + dir1_y * dir1_y);
                    const norm_dir1_x = dist1 > 0 ? dir1_x / dist1 : 0;
                    const norm_dir1_y = dist1 > 0 ? dir1_y / dist1 : 0;
                    new_v1_x = v1_x + norm_dir1_x * perp_movement * 0.5;
                    new_v1_y = v1_y + norm_dir1_y * perp_movement * 0.5;
                }
                
                if (is_right_angle_v2) {
                    // v2 is at right angle: move linearly (perpendicular to edge)
                    new_v2_x = v2_x + perp_movement * perp_unit_x;
                    new_v2_y = v2_y + perp_movement * perp_unit_y;
                } else {
                    // v2 not at right angle: move proportionally
                    const dir2_x = opp2_x - v2_x;
                    const dir2_y = opp2_y - v2_y;
                    const dist2 = Math.sqrt(dir2_x * dir2_x + dir2_y * dir2_y);
                    const norm_dir2_x = dist2 > 0 ? dir2_x / dist2 : 0;
                    const norm_dir2_y = dist2 > 0 ? dir2_y / dist2 : 0;
                    new_v2_x = v2_x + norm_dir2_x * perp_movement * 0.5;
                    new_v2_y = v2_y + norm_dir2_y * perp_movement * 0.5;
                }
            }

            // Create test points array with proposed new positions
            const test_points = [...panel.points];
            test_points[current_index * 2] = new_v1_x;
            test_points[current_index * 2 + 1] = new_v1_y;
            test_points[next_index * 2] = new_v2_x;
            test_points[next_index * 2 + 1] = new_v2_y;
            
            // Check if movement violates size, boundary limits, or causes inversion
            const min_size_valid = this.check_panel_minimum_size(test_points, 50);
            const bounds_valid = this.check_panel_within_bounds(panel, test_points);
            const orientation_valid = this.check_panel_orientation(test_points);

            let allow_movement = min_size_valid && bounds_valid && orientation_valid;
            
            //console.log(`Initial validation - min_size: ${min_size_valid}, bounds: ${bounds_valid}, orientation: ${orientation_valid}, allow_movement: ${allow_movement}`);
            
            // Handle persistent distance blocking for non-proportional mode
            if (!this.panel_edit_proportional_mode && handle) {
                const handle_id = handle._id;
                const handle_state = this.edge_drag_blocked_handles.get(handle_id);
                
                if (handle_state) {
                    // If handle is already blocked, check if movement is in opposite direction to unlock
                    if (handle_state.blocked) {
                        // Calculate current movement direction
                        const current_movement_vector = { x: perp_movement * perp_unit_x, y: perp_movement * perp_unit_y };
                        
                        // Check if movement is in opposite direction to initial blocking direction
                        if (handle_state.initial_direction) {
                            const dot_product = current_movement_vector.x * handle_state.initial_direction.x + 
                                              current_movement_vector.y * handle_state.initial_direction.y;
                            
                            // If dot product is negative, movement is in opposite direction - allow unlock
                            if (dot_product < 0) {
                                //console.log(`Movement in opposite direction detected, unlocking handle`);
                                handle_state.blocked = false;
                                handle_state.initial_direction = null;
                            } else {
                                // Still moving in blocked direction, maintain block
                                //console.log(`Handle remains blocked - movement in same direction`);
                                allow_movement = false;
                            }
                        }
                    } else {
                        // Handle not currently blocked, check if we should block it
                        if (!min_size_valid && bounds_valid && orientation_valid) {
                            const pointer_pos = this.get_pointer_position();
                            if (pointer_pos) {
                                const blocked_handle_x = panel.x() + (panel.points[current_index * 2] + panel.points[next_index * 2]) / 2;
                                const blocked_handle_y = panel.y() + (panel.points[current_index * 2 + 1] + panel.points[next_index * 2 + 1]) / 2;
                                
                                const distance_to_blocked_handle = Math.sqrt(
                                    Math.pow(pointer_pos.x - blocked_handle_x, 2) + 
                                    Math.pow(pointer_pos.y - blocked_handle_y, 2)
                                );
                                
                                const max_allowed_distance = 20;
                                //console.log(`Distance to blocked handle: ${distance_to_blocked_handle}, Max allowed: ${max_allowed_distance}`);
                                
                                if (distance_to_blocked_handle > max_allowed_distance) {
                                    // Activate blocking and store initial movement direction
                                    handle_state.blocked = true;
                                    handle_state.initial_direction = { 
                                        x: perp_movement * perp_unit_x, 
                                        y: perp_movement * perp_unit_y 
                                    };
                                    allow_movement = false;
                                    //console.log(`Distance block activated and locked`);
                                }
                            }
                        }
                    }
                }
            }
            
            //console.log(`Final allow_movement: ${allow_movement}`);
            
            // Only apply movement if validations pass
            if (allow_movement) {
                // Apply movement to vertices
                panel.points[current_index * 2] = new_v1_x;
                panel.points[current_index * 2 + 1] = new_v1_y;
                panel.points[next_index * 2] = new_v2_x;
                panel.points[next_index * 2 + 1] = new_v2_y;
                
                // Constrain handle to move only perpendicular to edge
                const constrained_x = orig_mid_x + perp_movement * perp_unit_x;
                const constrained_y = orig_mid_y + perp_movement * perp_unit_y;
                handle.x(panel.x() + constrained_x);
                handle.y(panel.y() + constrained_y);
                
                // Update panel shape and all handle positions
                this.update_panel_shape(panel);
                this.update_all_handles_position(panel);
            } else {
                // Movement would violate limits, keep handle at current edge midpoint
                const current_mid_x = (panel.points[current_index * 2] + panel.points[next_index * 2]) / 2;
                const current_mid_y = (panel.points[current_index * 2 + 1] + panel.points[next_index * 2 + 1]) / 2;
                handle.x(panel.x() + current_mid_x);
                handle.y(panel.y() + current_mid_y);
            }
        },

        update_edge_handles_position(panel) {
            if (!panel.points) return;
            
            const edge_handles = this.ui_layer.find('.panel_edge_handle');
            edge_handles.forEach(handle => {
                if (handle.getAttr('panel_id') === panel.id()) {
                    const edge_index = handle.getAttr('edge_index');
                    const next_index = (edge_index + 1) % (panel.points.length / 2);
                    
                    const x1 = panel.points[edge_index * 2];
                    const y1 = panel.points[edge_index * 2 + 1];
                    const x2 = panel.points[next_index * 2];
                    const y2 = panel.points[next_index * 2 + 1];
                    
                    const mid_x = (x1 + x2) / 2;
                    const mid_y = (y1 + y2) / 2;
                    handle.x(panel.x() + mid_x);
                    handle.y(panel.y() + mid_y);
                }
            });
        },

        update_all_handles_position(panel) {
            if (!panel.points) return;
            
            // Update vertex handles
            const vertex_handles = this.ui_layer.find('.panel_vertex_handle');
            vertex_handles.forEach(handle => {
                if (handle.getAttr('panel_id') === panel.id()) {
                    const node_index = handle.getAttr('node_index');
                    const x = panel.points[node_index * 2];
                    const y = panel.points[node_index * 2 + 1];
                    
                    handle.x(panel.x() + x);
                    handle.y(panel.y() + y);
                }
            });
            
            // Update edge handles
            this.update_edge_handles_position(panel);
            
            // Update center handle
            const center_handles = this.ui_layer.find('.panel_center_handle');
            center_handles.forEach(handle => {
                if (handle.getAttr('panel_id') === panel.id()) {
                    const bounds = this.get_panel_bounds(panel);
                    const center_x = bounds.minX + bounds.width / 2;
                    const center_y = bounds.minY + bounds.height / 2;
                    
                    handle.x(panel.x() + center_x);
                    handle.y(panel.y() + center_y);
                }
            });
            
            this.ui_layer.batchDraw();
        },

        // Calculate dimensions from aspect ratio and base size
        calculate_dimensions_from_aspect_ratio(aspect_ratio_id, base_size = 'medium') {
            const aspect_ratio_obj = this.aspect_ratios.find(ar => ar.id === aspect_ratio_id);
            if (!aspect_ratio_obj || aspect_ratio_id === 'custom') {
                return { width: 800, height: 1200 }; // Default fallback
            }

            const base = this.base_sizes[base_size] || this.base_sizes.medium;
            const ratio = aspect_ratio_obj.ratio;

            if (ratio >= 1) {
                // Landscape orientation (width > height)
                const width = Math.round(base.height * ratio);
                return { width: width, height: base.height };
            } else {
                // Portrait orientation (height > width)  
                const height = Math.round(base.width / ratio);
                return { width: base.width, height: height };
            }
        },

        // Update canvas clipping bounds when canvas dimensions change
        update_canvas_clipping() {
            if (this.panels_layer) {
                this.panels_layer.clipFunc((ctx) => {
                    ctx.rect(0, 0, this.canvas_width, this.canvas_height);
                });
            }
        },

    }
};